import { CosmicScene, BODY_3D, DEFAULT_TARGET } from './scene.js'
import { WarpTransition } from './warp.js'
import { PaleBlueDotSequence, SEQUENCE_START_TIME as SAGAN_START } from './pale-blue-dot.js'
import GUI from 'lil-gui'

const _isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const _isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

;(() => {
  const gate = document.getElementById('mobile-gate')
  if (!gate) return
  if (!_isTouchDevice) return

  if (_isIOS) document.body.classList.add('ios-device')

  const screenLong = Math.max(screen.width, screen.height)

  if (screenLong < 800) {
    document.body.classList.add('mobile-blocked')
    const txt = gate.querySelector('.mobile-gate-text')
    if (txt) txt.textContent = 'This experience requires a modern, high-resolution display. Please visit on a newer device or desktop to explore the Cosmos.'
    const phones = gate.querySelector('.mobile-gate-phones')
    if (phones) phones.style.display = 'none'
    return
  }

  let iosPromptShown = false

  function showIOSPrompt() {
    if (iosPromptShown || !_isIOS || navigator.standalone) return
    if (localStorage.getItem('ios-prompt-dismissed')) return
    iosPromptShown = true
    const iosPrompt = document.getElementById('ios-prompt')
    if (!iosPrompt) return
    iosPrompt.classList.add('visible')
    const closeBtn = document.getElementById('ios-prompt-close')
    if (closeBtn) closeBtn.addEventListener('click', () => {
      iosPrompt.classList.remove('visible')
      localStorage.setItem('ios-prompt-dismissed', '1')
    }, { once: true })
    setTimeout(() => {
      iosPrompt.classList.remove('visible')
    }, 15000)
  }

  function update() {
    const w = window.innerWidth
    const h = window.innerHeight
    if (h > w) {
      document.body.classList.add('mobile-blocked')
    } else {
      document.body.classList.remove('mobile-blocked')
      showIOSPrompt()
    }
  }

  window.addEventListener('resize', update)
  window.addEventListener('orientationchange', () => {
    setTimeout(update, 50)
    setTimeout(update, 200)
    setTimeout(update, 500)
  })
  update()
})()

const MUSIC_BASE = 'https://storage.googleapis.com/thecosmicstage-assets/music'

// ═══════════════════════════════════════════════════════════════
// LOGGING (filter console by "[CosmicStage]" to see app flow)
// ═══════════════════════════════════════════════════════════════
const log = (...args) => console.log('[CosmicStage]', ...args)
const warn = (...args) => console.warn('[CosmicStage]', ...args)
const err = (...args) => console.error('[CosmicStage]', ...args)

;(() => {
  const stars = '✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦'
  console.log(
    `\n%c${stars}\n\n` +
    `  🔭  Hey, you opened the console.  \n\n` +
    `  "Somewhere, something incredible\n` +
    `   is waiting to be known."\n` +
    `        — Carl Sagan\n\n` +
    `  ...but it's not in this source code, pal.\n\n` +
    `${stars}\n`,
    'color:#a78bfa;font-size:14px;font-family:monospace;line-height:1.6'
  )
  console.log(
    `%c  👀  Seriously though — you're peeking behind\n` +
    `  the curtain of the cosmos. Bold move.\n\n` +
    `  If you need help building something like this,\n` +
    `  or just want to talk about space, hit me up:\n\n` +
    `  📧  marinoonthelake@gmail.com\n\n` +
    `  "For small creatures such as we,\n` +
    `   the vastness is bearable only through love."\n` +
    `        — Carl Sagan\n\n` +
    `  Now close this and go look at the stars. 🌌\n`,
    'color:#60a5fa;font-size:12px;font-family:monospace;line-height:1.6'
  )
})()

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

function fmt(n, d) { return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }) }
function fmtInt(n) { return Math.floor(n).toLocaleString('en-US') }
function fmtBig(n) {
  const abs = Math.abs(n)
  if (abs >= 1e15) return fmt(n / 1e15, 1) + ' quadrillion'
  if (abs >= 1e12) return fmt(n / 1e12, 1) + ' trillion'
  if (abs >= 1e9)  return fmt(n / 1e9, 1)  + ' billion'
  if (abs >= 1e6)  return fmt(n / 1e6, 1)  + ' million'
  if (abs >= 1e3)  return fmtInt(n)
  return fmt(n, 1)
}

function calcAge(birthStr) {
  const birth = new Date(birthStr + 'T00:00:00')
  const now = new Date()
  const msPerDay = 86400000
  const days = (now - birth) / msPerDay
  const years = days / 365.256
  return { days, years, birth, now }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const CREDITS_MD = `§ 3D MODELS
"The Star Sun" (https://skfb.ly/oTDVZ) by ARCTIC WOLVES™ — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Mercury" (https://skfb.ly/6AOGX) by Yanez Designs — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Venus Realistic 8K" (https://skfb.ly/oKrFX) by Shady Tex — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Planet Earth" (https://skfb.ly/Ft8J) by Thomas Kole — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"The Moon" (https://skfb.ly/6s9oz) by SebastianSosnowski — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Mars" (https://skfb.ly/6Uynr) by AirStudios — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Ceres v1.1" (https://skfb.ly/6DuNu) by uperesito — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Saturn, Jupiter, Neptune, Uranus" (https://skfb.ly/orVqA) by Nestaeric — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Europa v1.1" (https://skfb.ly/6DtGo) by uperesito — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"Ganymede" (https://skfb.ly/onXKB) by SebastianSosnowski — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
"The Orrery" (https://skfb.ly/6VzKJ) by Amatsukast — CC BY-NC-SA 4.0 (https://creativecommons.org/licenses/by-nc-sa/4.0/)
§ TEXTURES
Skybox star map — Solar System Scope (https://www.solarsystemscope.com/textures/) — CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/)
§ CODE & INSPIRATION
Black hole effect inspired by Bruno Simon (https://github.com/brunosimon/webgl-black-hole)
Built with Three.js (https://threejs.org) — MIT License
Built with Vite (https://vitejs.dev) — MIT License
§ MUSIC
All original music composed and produced by Jonathan Marino`

function renderCredits(md) {
  return md.split('\n').filter(l => l.trim()).map(line => {
    if (line.startsWith('§ ')) {
      return `<h4 class="credit-section">${line.slice(2)}</h4>`
    }
    const linked = line.replace(
      /\(([^)]+)\)/g,
      (_, url) => url.startsWith('http') ? `(<a href="${url}" target="_blank" rel="noopener">${url}</a>)` : `(${url})`
    )
    return `<div class="credit-item">${linked}</div>`
  }).join('')
}

// ═══════════════════════════════════════════════════════════════
// JOURNEY BODY ORDER (outward from the Sun, then home)
// Sun → Mercury → Venus → The Moon → Mars → Ceres → Jupiter →
// Europa → Ganymede → Saturn → Ouranos → Neptune → Earth →
// The Voyage (orrery) → Stardust → Black Hole
// ═══════════════════════════════════════════════════════════════

const BODIES = [
  {
    id: 'sun', name: 'The Sun', sceneId: 'sun',
    ancients: 'Helios \u00b7 Sol \u00b7 S\u016brya \u00b7 Tonatiuh',
    tagline: 'Heart of the Solar System',
    altNames: ['Sol', 'Helios', 'Surya'],
    accent: '#ffcc30',
    facts(age) {
      const massLostKg = age.years * 1.35e17
      const massLostTons = massLostKg / 1000
      return [
        `Has lost <span class="value">${fmtBig(massLostTons)}</span> metric tons of mass since you were born`,
        `Converts <span class="value">600 million</span> tons of hydrogen into helium every second`,
        `Light from its surface takes <span class="value">8 minutes and 20 seconds</span> to reach your eyes`,
        `It is middle-aged \u2014 <span class="value">4.6 billion</span> years old, with roughly 5 billion left to burn`,
        `Its core burns at <span class="value">15 million \u00b0C</span> \u2014 the pressure and heat fuse atoms into light`,
      ]
    }
  },
  {
    id: 'mercury', name: 'Mercury', sceneId: 'mercury',
    ancients: 'Nabu \u00b7 Hermes \u00b7 Shu\u01d0x\u012bng \u00b7 Budha',
    tagline: 'The Swift Messenger \u2014 scorched and cratered',
    altNames: ['Hermes', 'Budha', 'Stilbon'],
    accent: '#b0b0b0',
    orbitalDays: 87.969, rotationDays: 58.646,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const solarDays = age.days / 176
      return [
        `<span class="value">${fmt(orbits, 1)}</span> orbits around the Sun`,
        `<span class="value">${fmt(solarDays, 1)}</span> Mercury solar days, each lasting 176 Earth days`,
        `Temperature swings from <span class="value">\u2212180\u00b0C</span> at night to <span class="value">430\u00b0C</span> by day \u2014 a 610\u00b0 range`,
        `The fastest planet, racing through space at <span class="value">170,000 km/h</span> \u2014 nearly 50 km every second`,
        `A 3:2 spin-orbit resonance \u2014 it rotates exactly <span class="value">3</span> times for every <span class="value">2</span> orbits`,
      ]
    }
  },
  {
    id: 'venus', name: 'Venus', sceneId: 'venus',
    ancients: 'Ishtar \u00b7 Phosphoros \u00b7 J\u012bnx\u012bng \u00b7 Shukra',
    tagline: 'Veiled in clouds — a furnace world',
    altNames: ['Aphrodite', 'Shukra', 'Lucifer'],
    accent: '#e8c547',
    orbitalDays: 224.701, rotationDays: 243.018,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const rotations = age.days / this.rotationDays
      return [
        `<span class="value">${fmt(orbits, 1)}</span> orbits around the Sun`,
        `Only <span class="value">${fmt(rotations, 1)}</span> rotations \u2014 its day (243 days) is longer than its year (225 days)`,
        `Surface temperature of <span class="value">462\u00b0C</span> \u2014 hot enough to melt lead, tin, and zinc`,
        `Atmospheric pressure <span class="value">92\u00d7</span> Earth\u2019s \u2014 equivalent to being 900 meters underwater`,
        `Spins <em>backwards</em> \u2014 the Sun rises in the west and sets in the east`,
        `You could outpace its rotation on foot \u2014 the equator moves at just <span class="value">6.5 km/h</span>`,
      ]
    }
  },
  {
    id: 'the_moon', name: 'The Moon', sceneId: 'the_moon',
    ancients: 'Luna \u00b7 Selene \u00b7 Chang\u2019e \u00b7 Chandra',
    tagline: 'Our constant companion \u2014 a fossil of creation',
    altNames: ['Luna', 'Selene', 'Cynthia'],
    accent: '#c0c0c0',
    facts(age) {
      const driftCm = age.years * 3.83
      const fullMoons = Math.floor(age.days / 29.53)
      const eclipses = Math.floor(age.years * 2.4)
      return [
        `<span class="value">${fmtInt(fullMoons)}</span> full moons have risen over the horizon since you were born`,
        `It has drifted <span class="value">${fmt(driftCm, 1)} cm</span> farther from Earth at 3.83 cm per year`,
        `Approximately <span class="value">${fmtInt(eclipses)}</span> solar and lunar eclipses have occurred in your lifetime`,
        `Born from a collision between Earth and a Mars-sized body <span class="value">4.5 billion</span> years ago`,
        `It is slowly stealing Earth\u2019s spin \u2014 days grow <span class="value">2.3 milliseconds</span> longer each century`,
        `One day, the Moon will be too far away to create total solar eclipses`,
      ]
    }
  },
  {
    id: 'mars', name: 'Mars', sceneId: 'mars',
    ancients: 'Ares \u00b7 Mangala \u00b7 Nergal \u00b7 Hu\u01d2x\u012bng',
    tagline: 'The Red Planet — dust and ancient rivers',
    altNames: ['Ares', 'Mangala', 'al-Qahira'],
    accent: '#e8522e',
    orbitalDays: 686.98, rotationDays: 1.02596,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const sols = age.days / this.rotationDays
      return [
        `<span class="value">${fmt(orbits, 1)}</span> orbits around the Sun`,
        `<span class="value">${fmtInt(sols)}</span> Martian sols have passed on the red planet`,
        `Olympus Mons \u2014 <span class="value">21.9 km</span> tall, nearly 3\u00d7 Everest \u2014 has stood in silence through all of them`,
        `Dust storms can grow large enough to engulf the entire planet for months`,
        `Valles Marineris stretches <span class="value">4,000 km</span> \u2014 it would span the entire United States`,
      ]
    }
  },
  {
    id: 'ceres', name: 'Ceres', sceneId: 'ceres',
    ancients: 'Cerere \u00b7 Demeter',
    tagline: 'Dwarf planet \u2014 queen of the asteroid belt',
    altNames: ['Demeter', '1 Ceres'],
    accent: '#a0a0a0',
    orbitalDays: 1681.63,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      return [
        `<span class="value">${fmt(orbits, 1)}</span> orbits around the Sun`,
        `At <span class="value">939 km</span> across, it holds <span class="value">~39%</span> of the entire asteroid belt\u2019s mass`,
        `Bright salt deposits on its surface hint at a subsurface ocean of briny water`,
        `It was the first asteroid ever discovered \u2014 found on January 1, <span class="value">1801</span>`,
        `Gravity is so faint you could jump <span class="value">30 meters</span> into the air`,
      ]
    }
  },
  {
    id: 'jupiter', name: 'Jupiter', sceneId: 'jupiter',
    ancients: 'Marduk \u00b7 Zeus \u00b7 M\u00f9x\u012bng \u00b7 Brihaspati',
    tagline: 'King of Planets — colossal and stormy',
    altNames: ['Jove', 'Zeus', 'Guru'],
    accent: '#e8a040',
    orbitalDays: 4332.59, rotationDays: 0.41354,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const jDays = age.days / this.rotationDays
      return [
        `<span class="value">${fmt(orbits, 2)}</span> orbits around the Sun`,
        `<span class="value">${fmtInt(jDays)}</span> Jupiter days \u2014 each only <span class="value">9.9 hours</span>, the shortest in the solar system`,
        `The Great Red Spot \u2014 a storm larger than Earth \u2014 has been raging for over <span class="value">350 years</span>`,
        `<span class="value">1,321</span> Earths could fit inside its volume`,
        `Its mass exceeds <span class="value">2.5\u00d7</span> all other planets combined`,
        `If it were <span class="value">80\u00d7</span> more massive, it would have ignited as a second Sun`,
      ]
    }
  },
  {
    id: 'europa', name: 'Europa', sceneId: 'europa',
    ancients: 'Moon of Jupiter \u00b7 The Ice Shell',
    tagline: 'An ocean world \u2014 hidden beneath miles of ice',
    altNames: ['Jupiter II'],
    accent: '#b0c8e0',
    facts(age) {
      return [
        `Beneath its cracked ice shell lies a liquid water ocean \u2014 <span class="value">twice</span> the volume of all Earth\u2019s oceans combined`,
        `The ice surface is only <span class="value">10\u201330 million</span> years old \u2014 one of the youngest in the solar system`,
        `Tidal flexing from Jupiter generates enough heat to keep the ocean liquid`,
        `Plumes of water vapor may erupt through the ice, reaching <span class="value">200 km</span> into space`,
        `It is the most likely place in the solar system to harbor extraterrestrial life`,
      ]
    }
  },
  {
    id: 'ganymede', name: 'Ganymede', sceneId: 'ganymede',
    ancients: 'Moon of Jupiter \u00b7 The Giant Moon',
    tagline: 'Largest moon in the solar system \u2014 bigger than Mercury',
    altNames: ['Jupiter III'],
    accent: '#908070',
    facts(age) {
      return [
        `At <span class="value">5,268 km</span> across, it is larger than the planet Mercury`,
        `The only moon with its own magnetic field \u2014 it generates faint auroras`,
        `A subsurface ocean may lie sandwiched between layers of ice, <span class="value">200 km</span> below`,
        `Its surface is split: ancient dark terrain cratered over <span class="value">4 billion</span> years, and younger grooved terrain`,
        `If it orbited the Sun instead of Jupiter, it would be classified as a planet`,
      ]
    }
  },
  {
    id: 'saturn', name: 'Saturn', sceneId: 'saturn',
    ancients: 'Ninurta \u00b7 Cronus \u00b7 T\u01d4x\u012bng \u00b7 Shani',
    tagline: 'The Ringed Wonder — jewel of the cosmos',
    altNames: ['Kronos', 'Shani', 'Sagush'],
    accent: '#e8d080',
    orbitalDays: 10759.22,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const ringLossKg = age.years * 3.15e11
      const ringLossTons = ringLossKg / 1000
      return [
        `<span class="value">${fmt(orbits, 3)}</span> orbits around the Sun`,
        `Its rings have lost approximately <span class="value">${fmtBig(ringLossTons)}</span> tons of ice since you were born`,
        `The rings stretch <span class="value">282,000 km</span> wide but are only <span class="value">10 meters</span> thick \u2014 a razor of ice and dust`,
        `Less dense than water \u2014 Saturn would float in an impossibly large ocean`,
        `<span class="value">146+</span> moons orbit it, including Titan, with its methane seas and thick atmosphere`,
        `Deep within, extreme pressure may forge falling diamonds from carbon`,
      ]
    }
  },
  {
    id: 'uranus', name: 'Ouranos', sceneId: 'uranus',
    ancients: 'O\u1f50\u03c1\u03b1\u03bd\u03cc\u03c2 \u00b7 Ti\u0101nw\u00e1ng-x\u012bng \u00b7 Father of Kronos',
    tagline: 'The Sideways World \u2014 tilted, frozen, primordial',
    altNames: ['Uranus', 'Georgium Sidus', 'Caelus'],
    accent: '#7de8e8',
    orbitalDays: 30688.5, rotationDays: 0.71833,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const pct = orbits * 100
      const uDays = age.days / this.rotationDays
      return [
        `<span class="value">${fmt(orbits, 4)}</span> orbits \u2014 just <span class="value">${fmt(pct, 1)}%</span> of a single year around the Sun`,
        `<span class="value">${fmtInt(uDays)}</span> days have spun past on the ice giant`,
        `Named for the primordial Greek sky god \u2014 father of Kronos, grandfather of Zeus`,
        `Tilted <span class="value">97.77\u00b0</span> on its axis \u2014 it rolls around the Sun on its side`,
        `Each of its seasons lasts <span class="value">21 Earth years</span>`,
        `Deep inside, extreme pressure may crush carbon into a rain of diamonds`,
      ]
    }
  },
  {
    id: 'neptune', name: 'Neptune', sceneId: 'neptune',
    ancients: 'H\u01cei-w\u00e1ng-x\u012bng \u00b7 Sea King Star',
    tagline: 'The Ice Giant — wind-whipped and distant',
    altNames: ['Poseidon', 'Varuna'],
    accent: '#5080e8',
    orbitalDays: 60182.0, rotationDays: 0.67125,
    facts(age) {
      const orbits = age.days / this.orbitalDays
      const pct = orbits * 100
      const nDays = age.days / this.rotationDays
      return [
        `<span class="value">${fmt(orbits, 4)}</span> orbits \u2014 only <span class="value">${fmt(pct, 1)}%</span> of a Neptunian year`,
        `<span class="value">${fmtInt(nDays)}</span> Neptune days have elapsed`,
        `Winds reach <span class="value">2,100 km/h</span> \u2014 the fastest in the solar system`,
        `It has not completed a single orbit since its discovery in <span class="value">1846</span>`,
        `Its moon Triton spirals ever closer, destined one day to shatter into a vast ring`,
      ]
    }
  },
  {
    id: 'earth', name: 'Earth', sceneId: 'earth',
    ancients: 'Gaia \u00b7 Terra \u00b7 J\u00f6r\u00f0 \u00b7 Prithvi',
    tagline: 'The Blue Marble \u2014 our pale blue dot',
    altNames: ['Terra', 'Gaia', 'Tellus'],
    accent: '#5cacff',
    orbitalDays: 365.256,
    facts(age) {
      const dayGrowthMs = age.years * 0.017
      const moonDriftCm = age.years * 3.83
      const heartbeats = age.years * 365.25 * 24 * 60 * 72
      const fullMoons = Math.floor(age.days / 29.53)
      const cosmicDustTons = age.years * 40000
      return [
        `<span class="value">${fmt(age.years, 1)}</span> orbits around the Sun \u2014 <span class="value">${fmtInt(age.days)}</span> days alive`,
        `Your heart has beaten approximately <span class="value">${fmtBig(heartbeats)}</span> times`,
        `<span class="value">${fmtInt(fullMoons)}</span> full moons have risen over the horizon`,
        `The day has grown <span class="value">${fmt(dayGrowthMs, 2)} ms</span> longer \u2014 the Moon is slowly stealing our spin`,
        `The Moon has drifted <span class="value">${fmt(moonDriftCm, 1)} cm</span> farther away at 3.83 cm per year`,
        `Earth has swept up ~<span class="value">${fmtBig(cosmicDustTons)}</span> tons of cosmic dust from space`,
      ]
    }
  },
  {
    id: 'solarsystem', name: 'The Voyage', sceneId: 'orrery',
    ancients: 'Our Solar System Through the Milky Way',
    accent: '#c090ff',
    isSolarSystem: true,
    facts(age) {
      const distKm = age.years * 7.26e9
      const distAU = distKm / 1.496e8
      const plutoAU = 39.5
      const plutoMultiple = distAU / plutoAU
      const moonDriftM = age.years * 0.0383
      const sunDriftM = age.years * 0.015
      return [
        `We have traveled <span class="value">${fmtBig(distKm)}</span> km through the Milky Way at <span class="value">230 km/s</span>`,
        `That\u2019s <span class="value">${fmtInt(distAU)}</span> AU \u2014 <span class="value">${fmt(plutoMultiple, 0)}\u00d7</span> the distance to Pluto\u2019s orbit`,
        `The Earth\u2013Sun gap has grown by <span class="value">${fmt(sunDriftM * 100, 1)} cm</span>. The Moon is <span class="value">${fmt(moonDriftM * 100, 1)} cm</span> farther.`,
        `The last time we were at this point in our galactic orbit, the first dinosaurs roamed the Earth`,
        `You were born in a different place in the galaxy. You can never return.`,
      ]
    }
  },
  {
    id: 'stardust', name: 'Your Stardust', sceneId: null,
    ancients: 'Expanding at the speed of light since the moment of your birth',
    accent: '#e0b0ff',
    isStardust: true,
    intro: 'At the moment you drew your first breath, something began that can never be undone. Your stardust started expanding outward from that singular point in spacetime, racing in every direction at 299,792 kilometers per second. It has not stopped. It will never stop.',
    closing: 'Long after every memory of you has faded from this world, your stardust will still be expanding\u2009\u2014\u2009touching stars that have not yet been born. The universe will carry the echo of your existence forever.',
    facts(age) {
      const radiusLY = age.years
      const diameterLY = radiusLY * 2
      const volumeLY3 = (4 / 3) * Math.PI * Math.pow(radiusLY, 3)
      const starsReached = Math.max(1, Math.floor(volumeLY3 * 0.003))
      const planetsEstimate = Math.floor(starsReached * 1.6)
      const habitableEstimate = Math.max(0, Math.floor(starsReached * 0.12))

      const notableStars = [
        { name: 'Alpha Centauri', dist: 4.37 },
        { name: 'Barnard\u2019s Star', dist: 5.96 },
        { name: 'Sirius', dist: 8.6 },
        { name: 'Epsilon Eridani', dist: 10.5 },
        { name: 'Procyon', dist: 11.4 },
        { name: 'Tau Ceti', dist: 11.9 },
        { name: 'Altair', dist: 16.7 },
        { name: 'Vega', dist: 25.0 },
        { name: 'Fomalhaut', dist: 25.0 },
        { name: 'Pollux', dist: 33.7 },
        { name: 'Arcturus', dist: 36.7 },
        { name: 'TRAPPIST-1', dist: 39.5 },
        { name: 'Capella', dist: 42.9 },
        { name: 'Castor', dist: 51.0 },
        { name: 'Aldebaran', dist: 65.3 },
      ]
      const reached = notableStars.filter(s => s.dist <= radiusLY)
      let starLine
      if (reached.length === 0) {
        const gap = 4.37 - radiusLY
        starLine = `Your light races toward Alpha Centauri, the nearest star system \u2014 <span class="value">${fmt(gap, 1)}</span> light-years to go`
      } else {
        const names = reached.map(s => s.name)
        const display = names.length > 6
          ? names.slice(0, 6).join(' \u00b7 ') + `, and <span class="value">${names.length - 6}</span> more`
          : names.join(' \u00b7 ')
        starLine = `Among them: ${display}`
      }
      return [
        `A sphere <span class="value">${fmt(diameterLY, 1)}</span> light-years wide \u2014 <span class="value">${fmt(radiusLY, 1)}</span> light-years in every direction`,
        `Approximately <span class="value">${fmtInt(starsReached)}</span> stars now lie within your stardust\u2019s embrace`,
        starLine,
        `An estimated <span class="value">${fmtInt(planetsEstimate)}</span> worlds orbit those stars`,
        `Perhaps <span class="value">${fmtInt(habitableEstimate)}</span> of them could harbor the conditions for life`,
      ]
    }
  },
  {
    id: 'blackhole', name: 'Beyond the Event Horizon', sceneId: null,
    ancients: 'Where spacetime ends and something else begins',
    tagline: 'The final curtain \u2014 gravity\u2019s ultimate triumph',
    accent: '#6030a0',
    isBlackHole: true,
    facts(age) {
      const photonsLost = age.years * 3.15e7 * 3e10
      return [
        `If you fell in, time would slow to a standstill from the outside \u2014 you would be frozen in light forever`,
        `The nearest known black hole, Gaia BH1, drifts <span class="value">1,560 light-years</span> away`,
        `Sagittarius A*, our galaxy\u2019s heart, is <span class="value">4 million</span> solar masses of pure curved spacetime`,
        `At the singularity, density becomes infinite \u2014 physics as we know it breaks down`,
        `Nothing escapes \u2014 not light, not time, not even information. Only Hawking radiation whispers back.`,
      ]
    }
  },
]

// ═══════════════════════════════════════════════════════════════
// GEMINI API — Dynamic Content Generation
// ═══════════════════════════════════════════════════════════════

const GEMINI_MODELS = ['gemini-2.5-flash', 'gemini-2.0-flash', 'gemini-2.5-pro']

let userBirthStr = null
const dynamicContentCache = new Map()

async function callGemini(prompt, cacheKey) {
  for (const model of GEMINI_MODELS) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 18000)
    try {
      const res = await fetch('/api/gemini', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ prompt, model, cacheKey })
      })
      clearTimeout(timeout)
      if (!res.ok) continue
      const data = await res.json()
      if (data.error) continue
      const parts = data.candidates?.[0]?.content?.parts || []
      const allText = parts.map(p => p.text || '').join('\n')
      if (!allText) continue

      const cleaned = allText.replace(/\`\`\`json\s*/g, '').replace(/\`\`\`\s*/g, '').trim()
      try { return JSON.parse(cleaned) } catch {}
      const match = allText.match(/\{[\s\S]*\}/)
      if (match) {
        try { return JSON.parse(match[0]) } catch {}
      }
      const arrMatch = allText.match(/\[[\s\S]*\]/)
      if (arrMatch) {
        try { return { missions: JSON.parse(arrMatch[0]) } } catch {}
      }
    } catch {
      clearTimeout(timeout)
    }
  }
  return null
}

// Ensure landmark recent missions ALWAYS appear on relevant slides regardless
// of what Gemini returned (training cutoff, hint ignored, stale cache, etc.)
const ARTEMIS_II = {
  the_moon_mission: { year: 2026, event: 'Artemis II rounded the Moon — farthest humans have ever traveled from Earth' },
  earth_mission: { year: 2026, event: 'Artemis II reached 406,000 km from Earth — the farthest humans have ever been' },
  solarsystem_mission: { year: 2026, event: "Artemis II rounded the Moon, humanity's farthest crewed voyage from Earth" },
  earth_event: { year: 2026, event_name: 'Return to the Moon', poetic_narration: 'For the first time in over fifty years, humans rode a rocket beyond low Earth orbit. Artemis II carried four astronauts around the far side of the Moon — 406,000 km from home, the farthest humans have ever been — and we remembered we could go.' },
  voyage_event: { year: 2026, event_name: 'Humans Return to Lunar Space', narration: 'Artemis II carried Wiseman, Glover, Koch, and Hansen around the Moon — the first crewed flight beyond low Earth orbit since Apollo 17. From 406,000 km away, four pairs of eyes watched Earth grow small again.' }
}

// Strip any event referencing the banned name, regardless of source
// (cached Gemini response, fresh Gemini response, fallback table).
const BANNED_PATTERN = /\btrump\b/i
function stripBanned(arr) {
  if (!Array.isArray(arr)) return arr
  return arr.filter(x => {
    if (!x) return false
    const blob = `${x.event || ''} ${x.event_name || ''} ${x.poetic_narration || ''} ${x.narration || ''}`
    return !BANNED_PATTERN.test(blob)
  })
}

function ensureLandmarkEvents(content, bodyId, birthYear) {
  if (!content || birthYear > 2026) return content

  if (content.missions) content.missions = stripBanned(content.missions)
  if (content.earth_events) content.earth_events = stripBanned(content.earth_events)
  if (content.voyage_events) content.voyage_events = stripBanned(content.voyage_events)

  const hasArtemis = (arr, key) => arr?.some(x => x?.year === 2026 && /artemis\s*ii/i.test(x[key] || ''))

  if (bodyId === 'the_moon') {
    content.missions = content.missions || []
    if (!hasArtemis(content.missions, 'event')) content.missions.push(ARTEMIS_II.the_moon_mission)
  } else if (bodyId === 'earth') {
    content.missions = content.missions || []
    content.earth_events = content.earth_events || []
    if (!hasArtemis(content.missions, 'event')) content.missions.push(ARTEMIS_II.earth_mission)
    if (!hasArtemis(content.earth_events, 'event_name')) content.earth_events.push(ARTEMIS_II.earth_event)
  } else if (bodyId === 'solarsystem') {
    content.missions = content.missions || []
    content.voyage_events = content.voyage_events || []
    if (!hasArtemis(content.missions, 'event')) content.missions.push(ARTEMIS_II.solarsystem_mission)
    if (!hasArtemis(content.voyage_events, 'event_name')) content.voyage_events.push(ARTEMIS_II.voyage_event)
  }

  if (content.missions) content.missions.sort((a, b) => (a.year || 0) - (b.year || 0))
  if (content.earth_events) content.earth_events.sort((a, b) => (a.year || 0) - (b.year || 0))
  if (content.voyage_events) content.voyage_events.sort((a, b) => (a.year || 0) - (b.year || 0))
  return content
}

function generateDynamicContent(bodyId) {
  if (dynamicContentCache.has(bodyId)) return dynamicContentCache.get(bodyId)
  const birthYear = userBirthStr ? new Date(userBirthStr + 'T00:00:00').getFullYear() : null
  const promise = _generateDynamicContentWithRetry(bodyId)
    .then(content => ensureLandmarkEvents(content, bodyId, birthYear))
  dynamicContentCache.set(bodyId, promise)
  return promise
}

async function _generateDynamicContentWithRetry(bodyId, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const result = await _generateDynamicContentImpl(bodyId)
    if (result) return result
    if (attempt < retries) await new Promise(r => setTimeout(r, 3000 * (attempt + 1)))
  }
  warn('Gemini content failed after retries for', bodyId, '— using static fallback')
  const birthDate = new Date(userBirthStr + 'T00:00:00')
  return getFallbackContent(bodyId, birthDate.getFullYear())
}

const MISSION_FALLBACKS = {
  sun: [
    { year: 1960, event: 'Pioneer 5 measured solar wind between Earth and Venus' },
    { year: 1974, event: 'Helios 1 launched to study the Sun at close range' },
    { year: 1976, event: 'Helios 2 set record for closest approach to the Sun' },
    { year: 1990, event: 'Ulysses spacecraft began studying the Sun\'s polar regions' },
    { year: 1995, event: 'SOHO launched, transforming our understanding of solar activity' },
    { year: 2006, event: 'STEREO provided first stereoscopic images of the Sun' },
    { year: 2010, event: 'SDO launched, capturing the Sun in ultra-high definition' },
    { year: 2018, event: 'Parker Solar Probe launched to touch the Sun\'s corona' },
    { year: 2020, event: 'Solar Orbiter launched for closest Sun imaging ever' },
    { year: 2021, event: 'Parker Solar Probe flew through the Sun\'s corona' },
  ],
  mercury: [
    { year: 1965, event: 'Radar revealed Mercury\'s true 59-day rotation period' },
    { year: 1974, event: 'Mariner 10 provided first close-up images of Mercury' },
    { year: 1975, event: 'Mariner 10 completed three Mercury flybys' },
    { year: 2004, event: 'MESSENGER launched on seven-year journey to Mercury' },
    { year: 2008, event: 'MESSENGER made first Mercury flyby in 33 years' },
    { year: 2011, event: 'MESSENGER entered Mercury orbit, mapping entire surface' },
    { year: 2012, event: 'MESSENGER discovered water ice in permanently shadowed craters' },
    { year: 2015, event: 'MESSENGER mission ended with controlled surface impact' },
    { year: 2018, event: 'BepiColombo launched on 7-year journey to Mercury' },
  ],
  venus: [
    { year: 1962, event: 'Mariner 2 became first spacecraft to fly by Venus' },
    { year: 1970, event: 'Venera 7 became first spacecraft to land on another planet' },
    { year: 1975, event: 'Venera 9 returned first photographs from Venus surface' },
    { year: 1982, event: 'Venera 13 captured first color photos of Venus surface' },
    { year: 1990, event: 'Magellan mapped 98% of Venus surface using radar' },
    { year: 2006, event: 'Venus Express entered orbit to study the atmosphere' },
    { year: 2015, event: 'Akatsuki entered Venus orbit after five-year recovery' },
    { year: 2020, event: 'Possible phosphine detection reignited Venus life debate' },
    { year: 2023, event: 'DAVINCI and VERITAS missions selected for future Venus exploration' },
  ],
  the_moon: [
    { year: 1959, event: 'Luna 3 captured first photographs of Moon\'s far side' },
    { year: 1966, event: 'Luna 9 achieved first soft landing on the Moon' },
    { year: 1969, event: 'Apollo 11 landed first humans on the Moon' },
    { year: 1972, event: 'Apollo 17 completed final crewed Moon mission' },
    { year: 1994, event: 'Clementine discovered ice deposits at lunar poles' },
    { year: 2009, event: 'LCROSS confirmed water ice in permanently shadowed craters' },
    { year: 2013, event: 'China\'s Chang\'e 3 landed first rover on Moon since 1976' },
    { year: 2019, event: 'Chang\'e 4 made first landing on the Moon\'s far side' },
    { year: 2023, event: 'India\'s Chandrayaan-3 landed near the lunar south pole' },
    { year: 2026, event: 'Artemis II rounded the Moon — farthest humans have ever traveled from Earth' },
  ],
  mars: [
    { year: 1965, event: 'Mariner 4 captured first close-up photos of Mars' },
    { year: 1971, event: 'Mariner 9 became first spacecraft to orbit another planet' },
    { year: 1976, event: 'Viking 1 and 2 landed, searching for life on Mars' },
    { year: 1997, event: 'Pathfinder and Sojourner rover landed on Mars surface' },
    { year: 2004, event: 'Spirit and Opportunity rovers began exploring Mars terrain' },
    { year: 2008, event: 'Phoenix lander confirmed water ice beneath Mars surface' },
    { year: 2012, event: 'Curiosity rover landed in Gale Crater, still exploring' },
    { year: 2015, event: 'NASA confirmed liquid briny water flows on Mars seasonally' },
    { year: 2021, event: 'Perseverance landed and Ingenuity achieved first powered flight' },
    { year: 2021, event: 'Perseverance began collecting rock samples for Earth return' },
  ],
  ceres: [
    { year: 1801, event: 'Giuseppe Piazzi discovered Ceres, the first asteroid ever found' },
    { year: 2006, event: 'Ceres reclassified as a dwarf planet by the IAU' },
    { year: 2007, event: 'Dawn spacecraft launched to study Vesta and Ceres' },
    { year: 2015, event: 'Dawn entered Ceres orbit revealing mysterious bright spots' },
    { year: 2016, event: 'Dawn discovered Ceres has subsurface ocean of briny water' },
    { year: 2017, event: 'Organic molecules detected on Ceres surface by Dawn' },
    { year: 2018, event: 'Dawn mission ended after 11 years exploring the asteroid belt' },
  ],
  jupiter: [
    { year: 1973, event: 'Pioneer 10 made first flyby of Jupiter' },
    { year: 1979, event: 'Voyager 1 and 2 revealed Jupiter\'s rings and volcanic Io' },
    { year: 1994, event: 'Comet Shoemaker-Levy 9 impacted Jupiter, watched worldwide' },
    { year: 1995, event: 'Galileo entered Jupiter orbit for eight years of study' },
    { year: 2003, event: 'Galileo plunged into Jupiter after 14 years of discovery' },
    { year: 2011, event: 'Juno launched to study Jupiter\'s deep atmosphere' },
    { year: 2016, event: 'Juno entered Jupiter orbit, revealing complex internal structure' },
    { year: 2022, event: 'JWST captured stunning infrared images of Jupiter\'s auroras' },
    { year: 2023, event: 'JUICE launched to study Jupiter\'s icy ocean moons' },
  ],
  europa: [
    { year: 1979, event: 'Voyager revealed Europa\'s cracked ice shell and smooth surface' },
    { year: 1997, event: 'Galileo detected magnetic signature of subsurface saltwater ocean' },
    { year: 2012, event: 'Hubble detected possible water plumes erupting from Europa' },
    { year: 2013, event: 'Scientists confirmed Europa\'s ocean contains hydrogen and oxygen' },
    { year: 2016, event: 'Hubble captured more evidence of water vapor plumes' },
    { year: 2024, event: 'Europa Clipper launched for detailed ocean world investigation' },
  ],
  ganymede: [
    { year: 1979, event: 'Voyager 1 revealed Ganymede\'s grooved terrain and size' },
    { year: 1996, event: 'Galileo discovered Ganymede has its own magnetic field' },
    { year: 2000, event: 'Galileo found evidence of subsurface saltwater ocean' },
    { year: 2015, event: 'Hubble confirmed vast underground ocean through aurora observations' },
    { year: 2021, event: 'Juno made closest flyby of Ganymede in two decades' },
    { year: 2023, event: 'JUICE launched with Ganymede orbit insertion as primary goal' },
  ],
  saturn: [
    { year: 1979, event: 'Pioneer 11 made first flyby of Saturn and its rings' },
    { year: 1980, event: 'Voyager 1 revealed Saturn\'s complex ring structure and moons' },
    { year: 1981, event: 'Voyager 2 provided detailed images of Saturn\'s atmosphere' },
    { year: 2004, event: 'Cassini entered Saturn orbit beginning 13-year mission' },
    { year: 2005, event: 'Huygens probe landed on Titan, revealing methane seas' },
    { year: 2006, event: 'Cassini discovered water geysers erupting from Enceladus' },
    { year: 2014, event: 'Cassini confirmed subsurface ocean inside Enceladus' },
    { year: 2017, event: 'Cassini\'s Grand Finale dove between Saturn and its rings' },
  ],
  uranus: [
    { year: 1977, event: 'Rings of Uranus discovered during a stellar occultation' },
    { year: 1986, event: 'Voyager 2 made first and only flyby of Uranus' },
    { year: 1997, event: 'Hubble discovered two new faint rings around Uranus' },
    { year: 2006, event: 'Hubble imaged Uranus\'s rings edge-on for the first time' },
    { year: 2007, event: 'Uranus reached equinox, rings illuminated by the Sun' },
    { year: 2022, event: 'JWST captured infrared images revealing Uranus ring details' },
    { year: 2023, event: 'Uranus flagship mission prioritized by planetary science decadal survey' },
  ],
  neptune: [
    { year: 1949, event: 'Gerard Kuiper discovered Nereid, Neptune\'s third-largest moon' },
    { year: 1984, event: 'Ground-based observations detected ring arcs around Neptune' },
    { year: 1989, event: 'Voyager 2 made first and only flyby of Neptune' },
    { year: 2003, event: 'Improved observations refined Neptune\'s mass and orbit data' },
    { year: 2005, event: 'New moons discovered orbiting Neptune using ground telescopes' },
    { year: 2013, event: 'Hubble discovered Neptune\'s 14th moon, Hippocamp' },
    { year: 2022, event: 'JWST captured first clear image of Neptune\'s rings in decades' },
  ],
  earth: [
    { year: 1957, event: 'Sputnik became the first artificial satellite orbiting Earth' },
    { year: 1961, event: 'Yuri Gagarin became the first human in space' },
    { year: 1969, event: 'Apollo 11 landed humans on the Moon for the first time' },
    { year: 1971, event: 'First space station Salyut 1 launched into Earth orbit' },
    { year: 1981, event: 'Space Shuttle Columbia launched the reusable spacecraft era' },
    { year: 1990, event: 'Hubble Space Telescope launched, transforming astronomy forever' },
    { year: 1998, event: 'International Space Station construction began in orbit' },
    { year: 2003, event: 'Columbia disaster led to profound changes in space safety' },
    { year: 2012, event: 'Felix Baumgartner broke sound barrier in freefall from space' },
    { year: 2020, event: 'SpaceX Crew Dragon launched first commercial crew to ISS' },
    { year: 2021, event: 'JWST launched, becoming most powerful space telescope ever' },
    { year: 2026, event: 'Artemis II reached 406,000 km from Earth — the farthest humans have ever been' },
  ],
  solarsystem: [
    { year: 1958, event: 'Explorer 1 discovered Earth\'s Van Allen radiation belts' },
    { year: 1962, event: 'Mariner 2 completed first successful planetary flyby at Venus' },
    { year: 1970, event: 'Venera 7 made first successful landing on another planet' },
    { year: 1977, event: 'Voyager 1 and 2 launched on grand tour of outer planets' },
    { year: 1990, event: 'Voyager 1 took Pale Blue Dot photo from 6 billion km' },
    { year: 1997, event: 'Cassini-Huygens launched on seven-year journey to Saturn' },
    { year: 2006, event: 'New Horizons launched toward Pluto and the Kuiper Belt' },
    { year: 2012, event: 'Voyager 1 crossed into interstellar space, still transmitting' },
    { year: 2015, event: 'New Horizons revealed Pluto\'s heart-shaped nitrogen glacier' },
    { year: 2018, event: 'Voyager 2 entered interstellar space 41 years after launch' },
    { year: 2026, event: 'Artemis II rounded the Moon, humanity\'s farthest crewed voyage from Earth' },
  ],
}

// "While You Were Here" — global lifetime events across politics, technology,
// science, culture, and society. Each entry has an `impact` rank (1–10).
// getFallbackContent picks the top 10 by impact within the user's lifetime,
// then displays chronologically. Pool is broad enough that a user born in
// 1945 and a user born in 2014 both get a meaningful, era-appropriate top 10.
const EARTH_EVENT_FALLBACKS = [
  // 1940s–1950s
  { year: 1945, event_name: 'The Atomic Age', poetic_narration: 'Humanity split the atom and the world held its breath. The power of stars was now in human hands.', impact: 10 },
  { year: 1948, event_name: 'The State of Israel', poetic_narration: 'A nation declared itself into existence three years after the camps were liberated. The Middle East has not stopped reckoning with that morning since.', impact: 8 },
  { year: 1953, event_name: 'The Double Helix', poetic_narration: 'Watson and Crick described the structure of DNA in a single page in Nature. Every cell in every body had been carrying the answer all along.', impact: 9 },
  { year: 1954, event_name: 'Brown v. Board of Education', poetic_narration: 'A unanimous Supreme Court declared segregated schools unconstitutional. The slow, hard unwinding of an old order had begun.', impact: 9 },
  { year: 1957, event_name: 'Sputnik', poetic_narration: 'A tiny sphere beeped its way across the sky, and humanity looked up with new eyes. The space age had begun.', impact: 8 },
  // 1960s
  { year: 1961, event_name: 'First Human in Space', poetic_narration: 'Gagarin rose above the atmosphere and saw the Earth whole for the first time. "I see Earth! It is so beautiful."', impact: 8 },
  { year: 1962, event_name: 'The Cuban Missile Crisis', poetic_narration: 'For thirteen days the world stood at the edge of the cliff and looked over. We are still here because two men chose, just barely, to step back.', impact: 9 },
  { year: 1963, event_name: 'A President in Dallas', poetic_narration: 'John F. Kennedy was killed on a Friday afternoon and a generation\'s certainty went with him. The country watched the funeral together and changed.', impact: 9 },
  { year: 1964, event_name: 'The Civil Rights Act', poetic_narration: 'A century after Emancipation, Congress finally outlawed discrimination in employment, schools, and public spaces. The law caught up to the conscience.', impact: 9 },
  { year: 1968, event_name: 'A Year That Cracked Open', poetic_narration: 'King and Kennedy were both shot dead within two months. Students filled the streets from Paris to Mexico City to Prague. The old certainties did not survive that summer.', impact: 9 },
  { year: 1969, event_name: 'The Moon Landing', poetic_narration: 'Two humans walked on another world while 600 million watched from this one. For a moment, the whole species held its breath together.', impact: 10 },
  // 1970s
  { year: 1973, event_name: 'The Oil Crisis', poetic_narration: 'OPEC cut off oil to the West and gas lines stretched for blocks. The age of cheap energy ended in a single autumn and the modern environmental movement found its starting gun.', impact: 8 },
  { year: 1973, event_name: 'Roe v. Wade', poetic_narration: 'The Supreme Court ruled that the Constitution protected the right to choose. The decision would shape American politics for fifty years and counting.', impact: 8 },
  { year: 1974, event_name: 'Nixon Resigns', poetic_narration: 'A president walked away from the office rather than be removed from it. Watergate had taught a country that no one was above the law.', impact: 8 },
  { year: 1975, event_name: 'The Fall of Saigon', poetic_narration: 'Helicopters lifted off the embassy roof while crowds pressed at the gates below. America\'s longest war ended in retreat, and a generation\'s trust ended with it.', impact: 8 },
  { year: 1977, event_name: 'Star Wars', poetic_narration: 'George Lucas\'s little space film opened on a few hundred screens and changed cinema, merchandise, and childhood for the rest of the century. The summer blockbuster was born.', impact: 7 },
  { year: 1978, event_name: 'The Test-Tube Baby', poetic_narration: 'Louise Brown was born in Manchester, the first child conceived outside a human body. Medicine had quietly taken over a job life had done alone for four billion years.', impact: 8 },
  { year: 1979, event_name: 'The Iranian Revolution', poetic_narration: 'A theocracy replaced the Shah and reshaped the Middle East overnight. Hostages held in Tehran would dominate American politics for the next 444 days.', impact: 8 },
  // 1980s
  { year: 1980, event_name: 'John Lennon', poetic_narration: 'A man with a gun waited outside the Dakota and shot a Beatle. The seventies ended that night, and a million strangers stood in Central Park together a few days later, candles and silence.', impact: 7 },
  { year: 1981, event_name: 'AIDS Identified', poetic_narration: 'A handful of unexplained deaths in young men became the defining epidemic of a generation. Medicine, art, and activism would all be made anew by it.', impact: 8 },
  { year: 1981, event_name: 'The IBM PC', poetic_narration: 'IBM put an open-architecture computer on the market and the personal computer became a category. Within a decade it had reached every desk and every home.', impact: 8 },
  { year: 1984, event_name: 'The Macintosh', poetic_narration: 'Apple introduced a computer for the rest of us — a beige box with a friendly face that put graphical computing on every desk.', impact: 7 },
  { year: 1985, event_name: 'Gorbachev\'s Reforms', poetic_narration: 'A new Soviet leader announced glasnost and perestroika and the empire began to open from within. The Cold War was about to end without a final battle.', impact: 9 },
  { year: 1986, event_name: 'Chernobyl', poetic_narration: 'A reactor in Soviet Ukraine melted and a cloud of invisible danger drifted across half a continent. The world learned a new vocabulary of fear.', impact: 7 },
  { year: 1986, event_name: 'The Challenger', poetic_narration: 'A shuttle exploded seventy-three seconds after liftoff while schoolchildren watched live. America paused its assumptions about space and looked up differently.', impact: 7 },
  { year: 1989, event_name: 'The Berlin Wall Falls', poetic_narration: 'A wall that divided a city, a country, and a world came down in a single night. Strangers embraced where guards had once stood.', impact: 10 },
  { year: 1989, event_name: 'Tiananmen Square', poetic_narration: 'A man stood in front of a column of tanks holding shopping bags and the world stopped to watch. We never learned his name.', impact: 8 },
  // 1990s
  { year: 1990, event_name: 'The World Wide Web', poetic_narration: 'Tim Berners-Lee published the first web page from CERN. Within a generation, every connection humans had ever made would route through that idea.', impact: 10 },
  { year: 1990, event_name: 'The Pale Blue Dot', poetic_narration: 'Voyager 1 turned its camera back from six billion kilometers and captured Earth as a mote of dust suspended in a sunbeam.', impact: 9 },
  { year: 1990, event_name: 'Hubble Launched', poetic_narration: 'A telescope rose above the atmosphere and gave humanity eyes that could see to the edge of time.', impact: 8 },
  { year: 1991, event_name: 'The Soviet Union Dissolves', poetic_narration: 'A red flag came down over the Kremlin and the Cold War ended without a final battle. Fifteen new nations stepped into the morning light.', impact: 10 },
  { year: 1994, event_name: 'Mandela\'s Inauguration', poetic_narration: 'A man imprisoned for twenty-seven years became the first democratically-elected president of South Africa. Apartheid ended without a civil war.', impact: 9 },
  { year: 1997, event_name: 'Princess Diana', poetic_narration: 'A princess died young in a Paris tunnel and a billion strangers grieved at once. The age of public mourning had begun.', impact: 6 },
  { year: 1998, event_name: 'The Space Station', poetic_narration: 'Nations that once aimed missiles at each other began building a home among the stars together.', impact: 7 },
  // 2000s
  { year: 2001, event_name: 'September 11', poetic_narration: 'Two towers fell on a clear blue Tuesday and a generation\'s sense of security fell with them. The world that woke up the next day was a different world entirely.', impact: 10 },
  { year: 2003, event_name: 'The Human Genome', poetic_narration: 'Scientists finished reading the three-billion-letter book that contains every human. Medicine has been turning the pages ever since.', impact: 9 },
  { year: 2004, event_name: 'The Indian Ocean Tsunami', poetic_narration: 'An undersea earthquake on the day after Christmas sent waves around the world that took 230,000 lives. The planet remembered how small we are.', impact: 7 },
  { year: 2004, event_name: 'Facebook', poetic_narration: 'A college site launched in a Harvard dorm room. Within a decade, more than a billion people would let it shape who they spoke to and what they saw.', impact: 8 },
  { year: 2005, event_name: 'YouTube Goes Live', poetic_narration: 'Three friends launched a site for sharing video and the word "broadcast" stopped meaning what it used to. Anyone could now reach everyone.', impact: 7 },
  { year: 2007, event_name: 'The iPhone', poetic_narration: 'Steve Jobs walked onstage with a glass slab and changed how every person on Earth would carry, see, and remember the world.', impact: 10 },
  { year: 2008, event_name: 'The Financial Crisis', poetic_narration: 'Lehman fell on a Monday and the global economy seized. The recession that followed reshaped a generation\'s relationship to work, home, and trust.', impact: 9 },
  { year: 2008, event_name: 'A New Era in the White House', poetic_narration: 'Barack Obama became the first Black president of the United States. A door that had been bolted for centuries swung open in a single November night.', impact: 9 },
  // 2010s
  { year: 2011, event_name: 'The Arab Spring', poetic_narration: 'A Tunisian fruit-seller set himself on fire and a chain reaction of revolutions swept across the Arab world. Squares filled, regimes fell, history bent.', impact: 8 },
  { year: 2012, event_name: 'The Higgs Boson', poetic_narration: 'CERN physicists confirmed the particle that gives matter its mass — the last piece of the Standard Model, found in a 27-kilometer ring under a French border.', impact: 8 },
  { year: 2015, event_name: 'Marriage Equality in the US', poetic_narration: 'The Supreme Court ruled that love is love and the law agreed at last. Couples married the same afternoon the decision came down.', impact: 8 },
  { year: 2015, event_name: 'The Paris Agreement', poetic_narration: 'One hundred and ninety-six nations signed a single climate pact in Paris. For the first time, the whole world admitted out loud what was happening to it.', impact: 8 },
  { year: 2016, event_name: 'Brexit', poetic_narration: 'Britain voted to leave the European Union — the first nation to step away from the postwar European project. The continent has been living in the aftermath of that referendum ever since.', impact: 7 },
  { year: 2016, event_name: 'Gravitational Waves Detected', poetic_narration: 'LIGO heard two black holes collide a billion years ago and a century later. Einstein had predicted this music; we finally built ears to hear it.', impact: 8 },
  { year: 2017, event_name: '#MeToo', poetic_narration: 'A hashtag gathered decades of silence into a single voice loud enough that no institution could pretend not to hear it.', impact: 8 },
  { year: 2019, event_name: 'The Climate Generation', poetic_narration: 'A teenager from Sweden sailed to New York to address the United Nations. Millions of students walked out of school behind her, and the generation born after 2000 made climate its inheritance.', impact: 7 },
  // 2020s
  { year: 2020, event_name: 'The Pandemic', poetic_narration: 'A virus stopped the world. Streets emptied, borders closed, and for the first time in living memory the entire human family went quiet at once.', impact: 10 },
  { year: 2020, event_name: 'A Global Reckoning on Race', poetic_narration: 'George Floyd\'s death sent millions into the streets of every continent. A movement that began in one Minneapolis intersection rewrote the language of policing, statues, and history.', impact: 8 },
  { year: 2021, event_name: 'JWST Launches', poetic_narration: 'The most powerful telescope ever built unfolded in space on Christmas Day, designed to see the first light of the universe.', impact: 8 },
  { year: 2022, event_name: 'ChatGPT Released', poetic_narration: 'OpenAI made a large language model free to the public and a hundred million people tried it in two months. Software that could draft text and code in any voice became standard equipment, and a debate over what it actually was — useful tool or counterfeit thought — began.', impact: 7 },
  { year: 2022, event_name: 'War Returns to Europe', poetic_narration: 'Russia invaded Ukraine and the postwar peace that had held a continent for seventy-seven years cracked. Europe rediscovered what it had hoped to forget.', impact: 9 },
  { year: 2022, event_name: 'Roe Overturned', poetic_narration: 'The Supreme Court reversed half a century of precedent and returned the question of abortion to the states. A right two generations had grown up with disappeared in a single ruling.', impact: 8 },
  { year: 2022, event_name: 'JWST\'s First Light', poetic_narration: 'The first deep-field images came back from a million miles past the Moon and showed galaxies thirteen billion years old. We had built an eye that could see the dawn.', impact: 7 },
  { year: 2023, event_name: 'Generative Tools Spread', poetic_narration: 'GPT-4, Midjourney, and a wave of generative tools moved from research labs into classrooms, offices, and creative studios. Whether the output qualified as thought stayed an open question; the pace of release did not.', impact: 6 },
  { year: 2024, event_name: 'The Great North American Eclipse', poetic_narration: 'A total solar eclipse swept from Mexico through Texas to Maine and into Canada. For four minutes, tens of millions of strangers on rooftops and in fields all looked up at the same darkened sun together.', impact: 7 },
  { year: 2026, event_name: 'Return to the Moon', poetic_narration: 'For the first time in over fifty years, humans rode a rocket beyond low Earth orbit. Artemis II carried four astronauts around the far side of the Moon — 406,000 km from home, the farthest humans have ever been — and we remembered we could go.', impact: 8 },
]

const VOYAGE_EVENT_FALLBACKS = [
  { year: 1962, event_name: 'First Planetary Visit', narration: 'Mariner 2 sailed past Venus and humanity touched another world with instruments for the first time. The solar system was no longer just a map.' },
  { year: 1969, event_name: 'Footprints on the Moon', narration: 'Armstrong and Aldrin walked where no life had ever stood. From the Moon, Earth looked fragile, precious, and impossibly alone.' },
  { year: 1977, event_name: 'The Grand Tour', narration: 'Voyager 1 and 2 embarked on a journey past every giant planet. They carry golden records of Earth, still sailing into the dark.' },
  { year: 1990, event_name: 'The Pale Blue Dot', narration: 'Voyager turned back one last time and photographed Earth from 6 billion km away. A pixel of light holding everyone who ever lived.' },
  { year: 2012, event_name: 'Into Interstellar Space', narration: 'Voyager 1 crossed the heliopause and entered the space between stars. Humanity\'s farthest envoy now drifts in the galactic wind.' },
  { year: 2015, event_name: 'Pluto Revealed', narration: 'New Horizons showed us Pluto\'s mountains and heart after a 9-year crossing. Even at the edge, the cosmos crafted beauty.' },
  { year: 2021, event_name: 'A New Eye Opens', narration: 'JWST launched on Christmas Day to see the first galaxies born. It looks back in time to the dawn of everything.' },
  { year: 2026, event_name: 'Humans Return to Lunar Space', narration: 'Artemis II carried Wiseman, Glover, Koch, and Hansen around the Moon—the first crewed flight beyond low Earth orbit since Apollo 17. From 406,000 km away, four pairs of eyes watched Earth grow small again.' },
]

// Pick `target` events with chronological coverage:
// round 1 takes the highest-impact event from each decade in the user's
// lifetime, then later rounds top up with the next-best events from any
// decade. Result is sorted by year for display. This prevents a 75-year-
// old's "top 10" from being dominated by recent watersheds while leaving
// 1970s/80s blank, and a young user's top 10 from being only the present.
function pickAcrossDecades(events, target) {
  if (events.length <= target) return events.slice().sort((a, b) => (a.year || 0) - (b.year || 0))
  const byDecade = new Map()
  for (const e of events) {
    const d = Math.floor((e.year || 0) / 10) * 10
    if (!byDecade.has(d)) byDecade.set(d, [])
    byDecade.get(d).push(e)
  }
  for (const arr of byDecade.values()) {
    arr.sort((a, b) => (b.impact || 5) - (a.impact || 5))
  }
  const decades = Array.from(byDecade.keys()).sort()
  const picked = []
  let round = 0
  while (picked.length < target) {
    let added = 0
    for (const d of decades) {
      if (picked.length >= target) break
      const candidate = byDecade.get(d)[round]
      if (candidate) { picked.push(candidate); added++ }
    }
    if (added === 0) break
    round++
  }
  return picked.sort((a, b) => (a.year || 0) - (b.year || 0))
}

function getFallbackContent(bodyId, birthYear) {
  const thisYear = new Date().getFullYear()
  if (bodyId === 'stardust' || bodyId === 'blackhole') return null

  if (bodyId === 'earth') {
    const missions = (MISSION_FALLBACKS.earth || [])
      .filter(m => m.year >= birthYear && m.year <= thisYear)
      .sort((a, b) => a.year - b.year)
      .slice(0, 5)
    const events = pickAcrossDecades(
      EARTH_EVENT_FALLBACKS.filter(e => e.year >= birthYear && e.year <= thisYear),
      10
    )
    if (!missions.length && !events.length) return null
    return { missions, earth_events: events }
  }

  if (bodyId === 'solarsystem') {
    const missions = (MISSION_FALLBACKS.solarsystem || [])
      .filter(m => m.year >= birthYear && m.year <= thisYear)
      .sort((a, b) => a.year - b.year)
      .slice(0, 5)
    const events = VOYAGE_EVENT_FALLBACKS
      .filter(e => e.year >= birthYear && e.year <= thisYear)
      .sort((a, b) => a.year - b.year)
      .slice(0, 3)
    if (!missions.length && !events.length) return null
    return { missions, voyage_events: events }
  }

  const allMissions = MISSION_FALLBACKS[bodyId]
  if (!allMissions) return null
  const filtered = allMissions
    .filter(m => m.year >= birthYear && m.year <= thisYear)
    .sort((a, b) => a.year - b.year)
    .slice(0, 5)
  if (!filtered.length) return null
  return { missions: filtered }
}

async function _generateDynamicContentImpl(bodyId) {
  if (!userBirthStr) return null
  const body = BODIES.find(b => b.id === bodyId)
  if (!body) return null

  const age = calcAge(userBirthStr)
  const birthDate = new Date(userBirthStr + 'T00:00:00')
  const birthYear = birthDate.getFullYear()
  const formattedBirth = birthDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const thisYear = new Date().getFullYear()
  const ageYears = Math.floor(age.years)

  // Recent ground-truth events that may post-date Gemini's training cutoff.
  // Listed here so the model includes them when relevant to the user's lifetime.
  const RECENT_EVENTS_HINT = `Recent ground-truth (consider when within user's lifetime):
- April 2026: Artemis II — first crewed lunar flight since Apollo 17 (1972). Wiseman, Glover, Koch, Hansen reached 406,771 km from Earth, farthest humans have ever traveled. April 1–10, 2026.
- 2022–2024: AI revolution. ChatGPT launched November 2022; GPT-4 March 2023; generative AI became ubiquitous in education, work, art, daily life.
- February 2022: Russia invaded Ukraine — first major land war in Europe since WWII.
- July 2022: JWST first deep-field images — earliest galaxies ever resolved, 13 billion light-years away.
- 2020–2021: COVID-19 pandemic. ~7 million confirmed deaths globally, mass remote-work shift, mRNA vaccines deployed in record time.`

  let prompt

  if (bodyId === 'earth') {
    const orbits = (age.days / 365.25).toFixed(1)
    const heartbeats = fmtBig(Math.round(age.years * 365.25 * 24 * 60 * 72))
    const fullMoons = Math.floor(age.days / 29.53)
    prompt = `You are "The Cosmic Historian," an AI that writes poetic, Sagan-esque narratives for a 3D solar system visualization called "The Cosmic Stage."

User Data: Born ${formattedBirth}, age ${ageYears} years (${fmtInt(age.days)} days).

TASK 1 \u2014 "While You Were Here" (earth_events):
Select 10 of the most globally impactful events that occurred during this person's lifetime. Mix categories \u2014 politics, science & technology, culture & arts, social movements, public health, breakthroughs, watersheds. NOT just space. Write a 2-sentence poetic narration for each, linking the event to their personal timeline.

Rules:
- Pick by global impact / influence, but ENSURE CHRONOLOGICAL SPREAD across the user's lifetime. Aim for at least one event from every decade they were alive. A 75-year-old's list with nothing between 1969 and 1989 is broken.
- Mix the categories \u2014 politics, science, technology, culture, social movements, public health. A list of 10 should feel like a time capsule of THEIR era, not a single domain.
- Scale framing to age. For older users, recent events should not dominate; their lifetime had its own watersheds (Civil Rights Act, Moon Landing, Watergate, Cold War endgame, AIDS crisis). For younger users, the most consequential events of THEIR lifetime are correctly recent.
- RETURN in chronological order (oldest year first).
- Be honest about technology. Large language models are useful tools, not consciousness; describe them factually, not as "AI awakening" or "machines beginning to think."
- Tone: awe-inspiring, concrete, considered. Avoid clich\u00e9s. Reference the user's age at the time when natural ("You were 19 when\u2026").
- For sensitive events (pandemics, wars, disasters), reference at a historical level \u2014 no lurid details.
- HARD BAN: Do not mention Donald Trump or any event tied to him in any form. If a year's biggest event was a Trump-related one, choose a different event from that year or skip the year entirely.

TASK 2 \u2014 "Humanity's Reach" (missions):
List 4-5 significant space missions or discoveries related to Earth observation and near-Earth space between ${birthYear} and ${thisYear}.

${RECENT_EVENTS_HINT}

Reference data: ~${orbits} orbits, ~${heartbeats} heartbeats, ~${fullMoons} full moons witnessed.

Return ONLY valid JSON (no markdown, no code fences, no explanation):
{"earth_events":[{"year":number,"event_name":"short title","poetic_narration":"Two sentences. Poetic and concrete."}],"missions":[{"year":number,"event":"max 12 words no period"}]}`
  } else if (bodyId === 'solarsystem') {
    prompt = `You are "The Cosmic Historian" for "The Cosmic Stage," a 3D solar system journey.

User Data: Born ${formattedBirth}, age ${ageYears}.

This is "The Voyage" \u2014 the final reflective scene. The user has just traveled from the Sun through every planet and now sees the entire solar system.

TASK 1 \u2014 "voyage_events": 3 poetic observations about humanity's space exploration during this person's lifetime. Each: 2 sentences linking cosmic exploration to their personal timeline. Reference their age at the time. Tone: Sagan-esque wonder, melancholic beauty.

Rules: Scale to age. Older users: Voyager, Apollo legacy, Hubble. Younger users: JWST, Mars rovers, interstellar probe milestones. Always awe-inspiring, never dark.

TASK 2 \u2014 "missions": 5 most significant solar system exploration milestones between ${birthYear} and ${thisYear}.

${RECENT_EVENTS_HINT}

Return ONLY valid JSON:
{"voyage_events":[{"year":number,"event_name":"short title","narration":"Two poetic sentences."}],"missions":[{"year":number,"event":"max 12 words no period"}]}`
  } else {
    const bodyDesc = `${body.name}${body.tagline ? ' \u2014 ' + body.tagline : ''}`
    prompt = `You are "The Cosmic Historian" for "The Cosmic Stage," a 3D solar system journey personalized to a visitor born in ${birthYear} (age ${ageYears}).

For ${bodyDesc}:
Generate 4-6 significant space missions, discoveries, or scientific events specifically about ${body.name} that occurred between ${birthYear} and ${thisYear}. Include mission names, key discoveries, and findings. Be historically accurate and specific. Order chronologically.

${RECENT_EVENTS_HINT}

Return ONLY valid JSON (no markdown, no code fences):
{"missions":[{"year":number,"event":"max 12 words no period"}]}`
  }

  const cacheKey = `${bodyId}:${birthYear}`
  const result = await callGemini(prompt, cacheKey)
  return result
}

function injectDynamicContent(slideIndex, content) {
  if (!content) return
  const slide = slidesEl.querySelectorAll('.slide')[slideIndex]
  if (!slide) return
  const bodyId = BODIES[slideIndex]?.id

  const missionsList = slide.querySelector('.dynamic-missions')
  if (missionsList && content.missions && Array.isArray(content.missions)) {
    missionsList.innerHTML = content.missions
      .filter(m => m && m.year && m.event)
      .map(m =>
        `<div class="human-fact"><span class="human-fact-year">${m.year}</span><span class="human-fact-text">${m.event}</span></div>`
      ).join('')
  }

  if (bodyId === 'earth' && content.earth_events && Array.isArray(content.earth_events)) {
    const eventsList = slide.querySelector('.dynamic-earth-events')
    if (eventsList) {
      eventsList.innerHTML = content.earth_events
        .filter(e => e && e.year)
        .map(e =>
          `<div class="earth-event">
            <span class="earth-event-year">${e.year}</span>
            <span class="earth-event-name">${e.event_name || ''}</span>
            <p class="earth-event-narration">${e.poetic_narration || ''}</p>
          </div>`
        ).join('')
    }
  }

  if (bodyId === 'solarsystem' && content.voyage_events && Array.isArray(content.voyage_events)) {
    const voyageList = slide.querySelector('.dynamic-voyage-events')
    if (voyageList) {
      voyageList.innerHTML = content.voyage_events
        .filter(v => v && v.year)
        .map(v =>
          `<div class="voyage-event">
            <span class="voyage-event-year">${v.year}</span>
            <span class="voyage-event-name">${v.event_name || ''}</span>
            <p class="voyage-event-narration">${v.narration || ''}</p>
          </div>`
        ).join('')
    }
  }
}


// SLIDE BUILDER
// ═══════════════════════════════════════════════════════════════

const slidesEl = document.getElementById('slides')
const dotsEl = document.getElementById('dots')
let currentSlide = 0
let transitioning = false

function buildSpecialPlanetHTML(body) {
  if (body.isSolarSystem) {
    const rings = [90, 130, 175, 210, 260, 310, 350, 400]
    return `<div class="slide-planet-visual"><div class="planet-sphere planet-solarsystem">${
      rings.map(r => `<div class="orbit-ring" style="width:${r}px;height:${r}px;"></div>`).join('')
    }</div></div>`
  }
  if (body.isStardust) {
    let inner = '<div class="stardust-glow"></div>'
    for (let j = 0; j < 6; j++) inner += '<div class="stardust-pulse"></div>'
    const rng = (function(s) { return function() { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646 } })(137)
    for (let j = 0; j < 20; j++) {
      const angle = rng() * Math.PI * 2
      const dist = 15 + rng() * 35
      const x = 50 + Math.cos(angle) * dist
      const y = 50 + Math.sin(angle) * dist
      const sz = 1 + rng() * 2
      const del = rng() * 2
      inner += `<div class="stardust-mote" style="width:${sz}px;height:${sz}px;left:${x}%;top:${y}%;animation-delay:${del.toFixed(1)}s"></div>`
    }
    inner += '<div class="stardust-core"></div>'
    return `<div class="slide-planet-visual"><div class="planet-sphere planet-stardust">${inner}</div></div>`
  }
  return ''
}

function buildJourney(birthStr) {
  const age = calcAge(birthStr)
  const formattedBirth = new Date(birthStr + 'T00:00:00')
    .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  slidesEl.innerHTML = ''
  dotsEl.innerHTML = ''
  userBirthStr = birthStr
  dynamicContentCache.clear()

  BODIES.forEach((body, i) => {
    const slide = document.createElement('div')
    slide.className = 'slide'
    slide.dataset.index = i
    slide.dataset.accent = body.accent

    const facts = body.facts ? body.facts(age) : []
    const factsHTML = facts.map(f => `<div class="fact">${f}</div>`).join('')
    const introHTML = body.intro ? `<div class="stardust-intro">${body.intro}</div>` : ''
    const closingHTML = body.closing ? `<div class="slide-closing">${body.closing}</div>` : ''
    const sectionTitle = body.isStardust ? 'Your Reach' : `Since ${formattedBirth}`

    const needsMissions = !body.isStardust && !body.isBlackHole
    const isEarth = body.id === 'earth'
    const isVoyage = body.id === 'solarsystem'

    let factsAreaHTML
    if (body.isStardust) {
      factsAreaHTML = `
        ${introHTML}
        <div class="slide-section-title">${sectionTitle}</div>
        <div class="facts-list">${factsHTML}</div>
        ${closingHTML}`
    } else if (isVoyage) {
      factsAreaHTML = `
        <div class="facts-columns">
          <div class="facts-column">
            <div class="slide-section-title">Your Voyage</div>
            <div class="facts-list">${factsHTML}</div>
            <div class="dynamic-voyage-events">
              <div class="dynamic-loading">Charting the voyage …</div>
            </div>
          </div>
          <div class="facts-column">
            <div class="slide-section-title">Humanity’s Reach</div>
            <div class="human-facts-list dynamic-missions">
              <div class="dynamic-loading">Consulting the cosmos …</div>
            </div>
          </div>
        </div>`
    } else if (isEarth) {
      factsAreaHTML = `
        <div class="facts-columns">
          <div class="facts-column">
            <div class="slide-section-title">${sectionTitle}</div>
            <div class="facts-list">${factsHTML}</div>
          </div>
          <div class="facts-column">
            <div class="slide-section-title">Humanity’s Reach</div>
            <div class="human-facts-list dynamic-missions">
              <div class="dynamic-loading">Consulting the cosmos …</div>
            </div>
            <div class="slide-section-title" style="margin-top:1.2em;animation-delay:1.7s">While You Were Here</div>
            <div class="dynamic-earth-events">
              <div class="dynamic-loading">Recalling your timeline …</div>
            </div>
          </div>
        </div>`
    } else if (needsMissions) {
      factsAreaHTML = `
        <div class="facts-columns">
          <div class="facts-column">
            <div class="slide-section-title">${sectionTitle}</div>
            <div class="facts-list">${factsHTML}</div>
          </div>
          <div class="facts-column">
            <div class="slide-section-title">Humanity’s Reach</div>
            <div class="human-facts-list dynamic-missions">
              <div class="dynamic-loading">Consulting the cosmos …</div>
            </div>
          </div>
        </div>`
    } else if (body.isBlackHole) {
      factsAreaHTML = `
        <div class="slide-section-title">${sectionTitle}</div>
        <div class="facts-list">${factsHTML}</div>`
    } else {
      factsAreaHTML = `
        <div class="slide-section-title">${sectionTitle}</div>
        <div class="facts-list">${factsHTML}</div>`
    }

    const specialVisual = !body.sceneId && !body.isBlackHole ? buildSpecialPlanetHTML(body) : ''
    const PLANET_ORDER_FROM_SUN = { mercury: 1, venus: 2, earth: 3, mars: 4, jupiter: 5, saturn: 6, uranus: 7, neptune: 8 }
    const planetNum = body.sceneId && PLANET_ORDER_FROM_SUN[body.sceneId]
    const slideNumHTML = planetNum != null ? `<span class="slide-number">${String(planetNum).padStart(2, '0')}</span>` : ''

    const taglineHTML = body.tagline ? `<p class="slide-tagline">${body.tagline}</p>` : ''
    slide.innerHTML = `
      ${specialVisual}
      <div class="slide-label">
        ${slideNumHTML}
        ${taglineHTML}
        <h2 class="slide-name">${body.name}</h2>
        <div class="slide-ancients">${body.ancients}</div>
      </div>
      <div class="slide-content">
        ${factsAreaHTML}
      </div>`

    slidesEl.appendChild(slide)

    const dot = document.createElement('div')
    dot.className = 'dot'
    dot.addEventListener('click', () => goToSlide(i))
    dotsEl.appendChild(dot)
  })

  const bhFloatBtn = document.getElementById('bh-mobile-restart')
  if (bhFloatBtn) {
    bhFloatBtn.addEventListener('click', () => showFinale())
  }
}

// ═══════════════════════════════════════════════════════════════
// FINALE OVERLAY
// ═══════════════════════════════════════════════════════════════

function showFinale() {
  const overlay = document.getElementById('finale-overlay')
  const creditsEl = document.getElementById('finale-credits')
  if (!overlay) return

  if (creditsEl && !creditsEl.innerHTML) {
    creditsEl.innerHTML = renderCredits(CREDITS_MD)
  }

  // Fade out all scene UI
  const journey = document.getElementById('journey')
  const nav = document.getElementById('nav')
  const editor = document.getElementById('editor-area')
  const scCanvas = document.getElementById('scene-canvas')
  const bhContainer = document.getElementById('blackhole-container')
  const mp = document.getElementById('ctrl-music-toggle')
  const fs = document.getElementById('ctrl-fullscreen')
  const wp = document.getElementById('ctrl-visualizer')
  const cf = document.getElementById('ctrl-coffee')
  const prog = document.getElementById('nav-progress')
  ;[journey, nav, editor, scCanvas, mp, fs, wp, cf, prog].forEach(el => { if (el) el.style.opacity = '0' })
  if (bhContainer) { bhContainer.style.transition = 'opacity 1.5s ease'; bhContainer.style.opacity = '0' }
  const bhMb = document.getElementById('bh-mobile-restart')
  if (bhMb) bhMb.classList.remove('visible')

  overlay.classList.add('visible')
}

function hideFinale() {
  const overlay = document.getElementById('finale-overlay')
  if (overlay) overlay.classList.remove('visible')

  const journey = document.getElementById('journey')
  const nav = document.getElementById('nav')
  const editor = document.getElementById('editor-area')
  const scCanvas = document.getElementById('scene-canvas')
  const bhContainer = document.getElementById('blackhole-container')
  const mp = document.getElementById('ctrl-music-toggle')
  const fs = document.getElementById('ctrl-fullscreen')
  const wp = document.getElementById('ctrl-visualizer')
  const cf = document.getElementById('ctrl-coffee')
  const prog = document.getElementById('nav-progress')
  ;[journey, nav, editor, scCanvas, bhContainer, mp, fs, wp, cf, prog].forEach(el => { if (el) el.style.opacity = '' })

  if (blackHoleInstance) {
    setTimeout(() => {
      const bhMb = document.getElementById('bh-mobile-restart')
      if (bhMb) bhMb.classList.add('visible')
    }, 1000)
  }
}

;(function initFinale() {
  const restartBtn = document.getElementById('finale-restart')
  if (restartBtn) {
    restartBtn.addEventListener('click', () => {
      window.location.reload()
    })
  }

  const goBackBtn = document.getElementById('finale-goback')
  if (goBackBtn) {
    goBackBtn.addEventListener('click', () => {
      hideFinale()
    })
  }
})()

// ═══════════════════════════════════════════════════════════════
// NAVIGATION WITH WARP TRANSITIONS
// ═══════════════════════════════════════════════════════════════

let cosmicScene = null
let warpTransition = null

let blackHoleInstance = null
let transitionStartedAt = 0
let transitionVersion = 0

function attachBlackHoleDriftDetection(container) {
  let bhInteracting = false
  let bhTimeout = null
  function bhFadeUI() {
    if (transitioning) return
    setFreeNavMode(true)
    returnBtn.classList.add('visible')
    bhInteracting = true
    clearTimeout(bhTimeout)
  }
  function bhRestoreUI() {
    bhInteracting = false
    clearTimeout(bhTimeout)
    bhTimeout = setTimeout(() => {
      if (!bhInteracting) {
        setFreeNavMode(false)
        returnBtn.classList.remove('visible')
      }
    }, 1200)
  }
  container.addEventListener('pointerdown', bhFadeUI, true)
  container.addEventListener('mousedown', bhFadeUI, true)
  container.addEventListener('touchstart', bhFadeUI, { capture: true, passive: true })
  container.addEventListener('pointerup', bhRestoreUI, true)
  container.addEventListener('mouseup', bhRestoreUI, true)
  container.addEventListener('touchend', bhRestoreUI, { capture: true, passive: true })
  container.addEventListener('pointercancel', bhRestoreUI, true)
  container.addEventListener('wheel', () => { bhFadeUI(); bhRestoreUI() }, { capture: true, passive: true })
}

async function goToSlide(i, { forceWarp = false } = {}) {
  log('goToSlide called:', i, 'current:', currentSlide, 'transitioning:', transitioning)
  if (i < 0 || i >= BODIES.length) return
  if (transitioning) {
    const stuckMs = transitionStartedAt ? Date.now() - transitionStartedAt : 0
    if (stuckMs > 12000) {
      warn('Force-resetting stuck transition after', stuckMs, 'ms')
      transitioning = false
    } else {
      log('goToSlide blocked by active transition for', stuckMs, 'ms')
      return
    }
  }
  if (i === currentSlide && slidesEl.querySelector('.slide.active')) return

  const isFirst = !forceWarp && !slidesEl.querySelector('.slide.active')
  transitioning = true
  transitionStartedAt = Date.now()
  const myVersion = ++transitionVersion
  try {
  cosmicScene.disableControls()
  hideReturnBtn()

  const slides = slidesEl.querySelectorAll('.slide')
  const dots = dotsEl.querySelectorAll('.dot')

  const newBody = BODIES[i]
  const needsDynamic = !newBody.isStardust && !newBody.isBlackHole

  // Fire off Gemini content generation early (parallel with warp + load)
  let contentPromise = null
  if (needsDynamic) {
    contentPromise = generateDynamicContent(newBody.id)
  }

  if (!isFirst && warpTransition) {
    const activeSlide = slides[currentSlide]
    if (activeSlide) activeSlide.classList.remove('active')
    await sleep(300)
    if (myVersion !== transitionVersion) throw 'stale'

    cosmicScene.darkenLights()
    showWarpFact()
    const navEl = document.getElementById('nav')
    const mp = document.getElementById('ctrl-music-toggle')
    const fs = document.getElementById('ctrl-fullscreen')
    const wp = document.getElementById('ctrl-visualizer')
    const cf = document.getElementById('ctrl-coffee')
    const editorArea = document.getElementById('editor-area')
    const ribbonEl = document.getElementById('scene-ribbon')
    const progressEl = document.getElementById('nav-progress')
    ;[navEl, mp, fs, wp, cf, editorArea, ribbonEl, progressEl].forEach(el => { if (el) el.classList.add('warp-hidden') })
    if (ribbonEl) { ribbonEl.classList.remove('open'); document.getElementById('nav-current')?.classList.remove('open') }
    returnBtn.classList.remove('visible')
    focusBtn.classList.remove('visible')
    const warpStart = performance.now()
    await warpTransition.engage()
    if (myVersion !== transitionVersion) throw 'stale'

    // Clean up black hole if leaving it
    if (blackHoleInstance) {
      blackHoleInstance.destroy()
      blackHoleInstance = null
      const bhContainer = document.getElementById('blackhole-container')
      if (bhContainer) bhContainer.remove()
      document.getElementById('scene-canvas').style.opacity = ''
      document.getElementById('scene-canvas').style.pointerEvents = ''
      document.body.classList.remove('blackhole-active')
      const bhMb = document.getElementById('bh-mobile-restart')
      if (bhMb) bhMb.classList.remove('visible')
    }

    let loadedGroup = null

    if (newBody.isBlackHole) {
      cosmicScene.removeCurrentBody()
      document.getElementById('scene-canvas').style.opacity = '0'
      document.getElementById('scene-canvas').style.pointerEvents = 'none'
      const container = document.createElement('div')
      container.id = 'blackhole-container'
      document.body.appendChild(container)
      try {
        const { mount } = await import('../Black Hole/sources/mount.js')
        blackHoleInstance = mount(container)
        attachBlackHoleDriftDetection(container)
        document.body.classList.add('blackhole-active')
      } catch (e) {
        warn('Black Hole mount failed', e)
        document.getElementById('scene-canvas').style.opacity = ''
        document.getElementById('scene-canvas').style.pointerEvents = ''
        container.remove()
      }
    } else if (newBody.sceneId) {
      const loadPromise = (async () => {
        const loadingEl = document.getElementById('loading-indicator')
        loadingEl.textContent = `Loading ${newBody.name}…`
        loadingEl.classList.add('visible')
        const group = await cosmicScene.loadBody(newBody.sceneId, (p) => {
          loadingEl.textContent = `Loading ${newBody.name} ${Math.round(p * 100)}%`
        })
        loadingEl.classList.remove('visible')
        loadingEl.textContent = 'Loading…'
        loadedGroup = group
      })()
      await loadPromise
    } else {
      cosmicScene.removeCurrentBody()
    }

    const MUSIC_FADE_AT_MS = 8000
    const elapsedBeforeFade = performance.now() - warpStart
    const fadeDelay = Math.max(0, MUSIC_FADE_AT_MS - elapsedBeforeFade)
    await sleep(fadeDelay)
    crossfadeToBody(newBody.id)

    const elapsed = performance.now() - warpStart
    const remaining = Math.max(0, MIN_WARP_HOLD_MS - elapsed)
    if (remaining > 0) await sleep(remaining)

    if (loadedGroup && newBody.sceneId) {
      cosmicScene.showBody(loadedGroup, newBody.sceneId)
    }

    // Hold warp until Gemini content is ready (or we hit the cap), so the
    // slide reveals with content rather than "Consulting the cosmos…"
    if (contentPromise) {
      const MAX_WARP_TOTAL_MS = 18000
      const elapsedForContent = performance.now() - warpStart
      const remainingBudget = Math.max(0, MAX_WARP_TOTAL_MS - elapsedForContent)
      if (remainingBudget > 0) {
        await Promise.race([
          contentPromise.catch(() => null),
          sleep(remainingBudget)
        ])
      }
    }

    hideWarpFact()
    await warpTransition.disengage()
    if (myVersion !== transitionVersion) throw 'stale'

    ;[navEl, mp, fs, wp, cf, editorArea, ribbonEl, progressEl].forEach(el => { if (el) el.classList.remove('warp-hidden') })

    if (newBody.sceneId) {
      cosmicScene.fadeInLights(newBody.sceneId)
    }

    if (newBody.isBlackHole) {
      setTimeout(() => {
        const bhMb = document.getElementById('bh-mobile-restart')
        if (bhMb) bhMb.classList.add('visible')
      }, 1500)
    }
  } else if (isFirst) {
    if (newBody.isBlackHole) {
      document.getElementById('scene-canvas').style.opacity = '0'
      document.getElementById('scene-canvas').style.pointerEvents = 'none'
      const container = document.createElement('div')
      container.id = 'blackhole-container'
      document.body.appendChild(container)
      try {
        const { mount } = await import('../Black Hole/sources/mount.js')
        if (myVersion !== transitionVersion) throw 'stale'
        blackHoleInstance = mount(container)
        attachBlackHoleDriftDetection(container)
        document.body.classList.add('blackhole-active')
      } catch (e) {
        if (e === 'stale') throw e
        warn('Black Hole mount failed', e)
      }
    } else if (newBody.sceneId) {
      const group = await cosmicScene.loadBody(newBody.sceneId)
      if (myVersion !== transitionVersion) throw 'stale'
      if (group) {
        cosmicScene.showBody(group, newBody.sceneId)
        cosmicScene.darkenLights()
        cosmicScene.fadeInLights(newBody.sceneId)
      }
    }
  }

  slides.forEach(s => { s.classList.remove('active'); s.classList.remove('copy-revealed') })
  dots.forEach(d => d.classList.remove('active'))

  const target = slides[i]
  target.classList.add('active')
  dots[i].classList.add('active')
  currentSlide = i
  const navCurrent = document.getElementById('nav-current')
  const navName = document.getElementById('nav-current-name')
  if (navCurrent && navName) {
    navName.textContent = BODIES[i]?.name || ''
    if (navCurrent.disabled) {
      navCurrent.disabled = false
      navCurrent.classList.add('unlocked')
      document.getElementById('nav-progress')?.classList.add('unlocked')
    }
  }
  document.querySelectorAll('.scene-ribbon-item').forEach((el, idx) => {
    el.classList.toggle('active', idx === i)
  })
  const accent = BODIES[i]?.accent || '#fff'
  document.querySelectorAll('.nav-progress-dot').forEach((el, idx) => {
    if (idx === i) {
      el.classList.add('active')
      el.style.setProperty('--dot-accent', accent)
    } else {
      el.classList.remove('active')
    }
  })
  resetFocusBtn()

  const prevBtn = document.getElementById('prev-btn')
  const nextBtn = document.getElementById('next-btn')

  if (i === 0) {
    prevBtn.disabled = true
    prevBtn.innerHTML = '&#8592;'
  } else {
    prevBtn.disabled = false
    prevBtn.innerHTML = `&#8592; <span class="nav-label">${BODIES[i - 1].name}</span>`
  }

  if (i === BODIES.length - 1) {
    nextBtn.disabled = true
    nextBtn.innerHTML = '&#8594;'
  } else {
    nextBtn.innerHTML = `<span class="nav-label">${BODIES[i + 1].name}</span> &#8594;`
    nextBtn.disabled = false
  }

  document.documentElement.style.setProperty('--accent', BODIES[i].accent)

  await sleep(400)
  target.classList.add('copy-revealed')

  if (contentPromise) {
    contentPromise.then(content => {
      if (content) {
        injectDynamicContent(i, content)
      } else {
        const slide = slidesEl.querySelectorAll('.slide')[i]
        if (slide) {
          slide.querySelectorAll('.dynamic-loading').forEach(el => {
            el.textContent = 'Content unavailable — try refreshing'
            el.style.opacity = '0.3'
          })
        }
      }
    })
  }

  cosmicScene.enableControls()
  cosmicScene.markCameraHome()
  if (sceneEditorSync && BODIES[i].sceneId) sceneEditorSync()
  } catch (e) {
    if (e !== 'stale') err('goToSlide error:', e)
  } finally {
    if (myVersion === transitionVersion) {
      transitioning = false
      transitionStartedAt = 0
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// CAMERA DRIFT → TEXT FADE + RETURN BUTTON
// ═══════════════════════════════════════════════════════════════

const DRIFT_THRESHOLD = 0.15
const returnBtn = document.getElementById('return-btn')
const focusBtn = document.getElementById('focus-btn')
let journeyActive = false
let ctaActive = false
let isFocusedOnBody = false

function getFocusLabel() {
  const body = BODIES[currentSlide]
  if (!body) return 'Focus'
  if (body.isBlackHole) return 'Focus Black Hole'
  if (body.isStardust) return 'Focus Stardust'
  if (body.isSolarSystem) return 'Focus Orrery'
  const sceneId = body.sceneId
  if (!sceneId) return 'Focus'
  const data = BODY_3D[sceneId]
  if (!data) return 'Focus'
  const labels = { star: 'Star', planet: 'Planet', moon: 'Moon', dwarf: 'Dwarf Planet', orrery: 'Orrery', blackhole: 'Black Hole' }
  return 'Focus ' + (labels[data.type] || 'Planet')
}

function setFreeNavMode(on) {
  const label = document.querySelector('.slide.active .slide-label')
  const content = document.querySelector('.slide.active .slide-content')
  const navEl = document.getElementById('nav')
  const mp = document.getElementById('ctrl-music-toggle')
  const fs = document.getElementById('ctrl-fullscreen')
  const wp = document.getElementById('ctrl-visualizer')
  const cf = document.getElementById('ctrl-coffee')
  const editorArea = document.getElementById('editor-area')
  const ribbonEl = document.getElementById('scene-ribbon')
  const progressEl = document.getElementById('nav-progress')
  const cls = 'camera-faded'
  ;[label, content, navEl, mp, fs, wp, cf, editorArea, ribbonEl, progressEl].forEach(el => {
    if (el) el.classList[on ? 'add' : 'remove'](cls)
  })
  if (on && ribbonEl) { ribbonEl.classList.remove('open'); document.getElementById('nav-current')?.classList.remove('open') }
}

function fadeSlideText(faded) { setFreeNavMode(faded) }

function showNavUI() {
  document.getElementById('nav').classList.add('visible')
  document.getElementById('nav-progress').classList.add('visible')
}

function resetFocusBtn() {
  isFocusedOnBody = false
  focusBtn.classList.remove('focused')
  const label = getFocusLabel()
  focusBtn.innerHTML = `&#9678; ${label}`
}

function hideReturnBtn() {
  returnBtn.classList.remove('visible')
  focusBtn.classList.remove('visible')
  resetFocusBtn()
  setFreeNavMode(false)
}

function setupDriftDetection() {
  cosmicScene.setDriftCallback((drift) => {
    if (transitioning) return

    if (journeyActive) {
      if (drift > DRIFT_THRESHOLD) {
        setFreeNavMode(true)
        returnBtn.classList.add('visible')
        focusBtn.classList.add('visible')
      } else {
        setFreeNavMode(false)
        returnBtn.classList.remove('visible')
        focusBtn.classList.remove('visible')
        if (isFocusedOnBody) resetFocusBtn()
      }
    } else if (ctaActive) {
      const ctaPanel = document.getElementById('cta-phase')
      const mp = document.getElementById('ctrl-music-toggle')
      const fs = document.getElementById('ctrl-fullscreen')
      const wp = document.getElementById('ctrl-visualizer')
      const cf = document.getElementById('ctrl-coffee')
      const editorArea = document.getElementById('editor-area')
      const cls = 'camera-faded'
      if (drift > DRIFT_THRESHOLD) {
        returnBtn.classList.add('visible')
        focusBtn.classList.add('visible')
        ;[ctaPanel, mp, fs, wp, cf, editorArea].forEach(el => { if (el) el.classList.add(cls) })
      } else {
        returnBtn.classList.remove('visible')
        focusBtn.classList.remove('visible')
        if (isFocusedOnBody) resetFocusBtn()
        ;[ctaPanel, mp, fs, wp, cf, editorArea].forEach(el => { if (el) el.classList.remove(cls) })
      }
    }
  })

  focusBtn.addEventListener('click', () => {
    if (!isFocusedOnBody) {
      cosmicScene.focusOnBody()
      isFocusedOnBody = true
      focusBtn.classList.add('focused')
      focusBtn.innerHTML = '&#9678; Focused'
    } else {
      cosmicScene.resetToHome()
      resetFocusBtn()
      returnBtn.classList.remove('visible')
      focusBtn.classList.remove('visible')
      fadeSlideText(false)
    }
  })

  returnBtn.addEventListener('click', () => {
    cosmicScene.resetToHome()
    returnBtn.classList.remove('visible')
    focusBtn.classList.remove('visible')
    resetFocusBtn()
    fadeSlideText(false)
  })
}

// ═══════════════════════════════════════════════════════════════
// NAVIGATION EVENTS
// ═══════════════════════════════════════════════════════════════

document.getElementById('prev-btn').addEventListener('click', () => goToSlide(currentSlide - 1))
document.getElementById('next-btn').addEventListener('click', () => goToSlide(currentSlide + 1))

function showInteractHint() {
  const hint = document.getElementById('cta-interact-hint')
  if (!hint) return
  setTimeout(() => hint.classList.add('visible'), 1000)
  const fadeOnInteract = () => {
    hint.classList.add('fade-out')
    document.removeEventListener('pointerdown', fadeOnInteract)
    document.removeEventListener('pointermove', fadeOnDrag)
  }
  const fadeOnDrag = (e) => {
    if (e.buttons > 0) fadeOnInteract()
  }
  document.addEventListener('pointerdown', fadeOnInteract)
  document.addEventListener('pointermove', fadeOnDrag)
}
function hideInteractHint() {
  const hint = document.getElementById('cta-interact-hint')
  if (hint) hint.classList.add('fade-out')
}

// ═══════════════════════════════════════════════════════════════
// INTRO SEQUENCE
// Phase 1: Pale blue dot fades in
// Phase 2: Earth age text
// Phase 3: "When did you arrive?" prompt
// Phase 4: Fade to black, reveal Earth scene, CTA panel
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// MUSIC
// ═══════════════════════════════════════════════════════════════

const MUSIC_MAP = {
  sun: 'Helios', mercury: 'Mercury', venus: 'Venus', earth: 'Intro',
  mars: 'Ares', ceres: 'Ceres', jupiter: 'Jupiter',
  europa: 'Europa', ganymede: 'Ganymede', saturn: 'Saturn',
  uranus: 'Ouranos', neptune: 'Neptune', the_moon: 'Luna',
  solarsystem: 'Orrery', stardust: 'Stardust',
  blackhole: 'BlackHole',
}

let currentAudio = null
let musicEnabled = true
const FADE_MS = 1500
const CROSSFADE_MS = 6000
const MIN_WARP_HOLD_MS = 10000

const _audioPool = []
let _audioPoolIdx = 0

function _initAudioPool() {
  if (_audioPool.length) return
  for (let i = 0; i < 2; i++) {
    const a = new Audio()
    a.crossOrigin = 'anonymous'
    _audioPool.push(a)
  }
}

function createConnectedAudio(src) {
  _initAudioPool()
  const audio = _audioPool[_audioPoolIdx % 2]
  _audioPoolIdx++
  audio.pause()
  audio.volume = 0
  audio.src = src
  audio.load()
  return audio
}

function fadeOutAudio(audio, ms = FADE_MS) {
  if (!audio) return Promise.resolve()
  return new Promise(resolve => {
    const start = audio.volume
    const steps = 30
    const interval = ms / steps
    let step = 0
    const timer = setInterval(() => {
      step++
      audio.volume = Math.max(0, start * (1 - step / steps))
      if (step >= steps) {
        clearInterval(timer)
        audio.pause()
        resolve()
      }
    }, interval)
  })
}

function fadeInAudio(audio, targetVol = 0.5, ms = FADE_MS) {
  if (!audio) return
  audio.volume = 0
  audio.play().catch(() => {})
  const steps = 30
  const interval = ms / steps
  let step = 0
  const timer = setInterval(() => {
    step++
    audio.volume = Math.min(targetVol, targetVol * (step / steps))
    if (step >= steps) clearInterval(timer)
  }, interval)
}

function playTrackForBody(bodyId) {
  if (!musicEnabled) return
  const trackName = MUSIC_MAP[bodyId]
  if (!trackName) return
  const src = `${MUSIC_BASE}/${trackName}.mp3`
  if (currentAudio && currentAudio._trackSrc === src) return
  if (currentAudio) fadeOutAudio(currentAudio, FADE_MS)
  const audio = createConnectedAudio(src)
  audio.loop = true
  audio._trackSrc = src
  fadeInAudio(audio, 0.45, FADE_MS)
  currentAudio = audio
  if (window._updateMusicUI) setTimeout(window._updateMusicUI, 200)
}

function crossfadeToBody(bodyId) {
  if (!musicEnabled) return
  const trackName = MUSIC_MAP[bodyId]
  if (!trackName) {
    if (currentAudio) fadeOutAudio(currentAudio, CROSSFADE_MS / 2)
    currentAudio = null
    if (window._updateMusicUI) setTimeout(window._updateMusicUI, 200)
    return
  }
  const src = `${MUSIC_BASE}/${trackName}.mp3`
  if (currentAudio && currentAudio._trackSrc === src) return

  const oldAudio = currentAudio
  const newAudio = createConnectedAudio(src)
  newAudio.loop = true
  newAudio._trackSrc = src

  fadeOutAudio(oldAudio, CROSSFADE_MS)
  fadeInAudio(newAudio, 0.45, CROSSFADE_MS)
  currentAudio = newAudio
  if (window._updateMusicUI) setTimeout(window._updateMusicUI, 200)
}

// ═══════════════════════════════════════════════════════════════
// WARP FACTS
// ═══════════════════════════════════════════════════════════════

const WARP_FACTS = [
  { title: 'Gravity isn\u2019t a \u201cpull\u201d', text: 'Mass bends the fabric of spacetime. The Earth isn\u2019t pulling you down \u2014 it has warped the space around it, and your body follows the natural curved path.' },
  { title: 'The speed of causality', text: 'Light travels at 299,792,458 m/s, but that limit isn\u2019t about light \u2014 it\u2019s the maximum speed at which cause and effect can propagate. If the Sun vanished, we wouldn\u2019t know for 8 minutes and 20 seconds.' },
  { title: 'Time dilation', text: 'Because the speed of light is constant for everyone, time itself has to flex. Travel near light speed for a few years and return to an Earth where decades or centuries have passed.' },
  { title: 'Gravitational waves', text: 'When black holes or neutron stars collide, they send ripples through spacetime. As these waves pass through you, the physical space you occupy briefly stretches and squeezes \u2014 on a scale smaller than an atom.' },
  { title: 'Frame-dragging', text: 'A rapidly spinning black hole drags the fabric of spacetime around with it, forcing everything nearby to orbit along with the twist.' },
  { title: 'Wormholes', text: 'Einstein-Rosen bridges are hypothetical tunnels connecting two distant points in spacetime. The math allows for them, but they may require exotic negative mass to keep from collapsing.' },
  { title: 'Spaghettification', text: 'Fall feet-first into a black hole, and the gravity on your feet is vastly stronger than on your head. Tidal forces would stretch you into a long, thin string of atoms.' },
  { title: 'White holes', text: 'The mathematical opposite of black holes \u2014 a region of space into which nothing can enter, violently spewing matter and light outward.' },
  { title: 'Neutron stars', text: 'When a massive star goes supernova, the core collapses until protons and electrons fuse into neutrons. A teaspoon of this material weighs about 10 million tons on Earth.' },
  { title: 'The Holographic Principle', text: 'The entire three-dimensional universe might be a projection of two-dimensional information encoded on a distant boundary \u2014 a hypothesis born from studying black hole event horizons.' },
  { title: 'Looking up is looking back in time', text: 'Light takes time to travel. You see the Moon 1.3 seconds ago, the nearest star 4 years ago, and Andromeda 2.5 million years ago.' },
  { title: 'Faster than light \u2014 sort of', text: 'Nothing in space can outrun light, but space itself has no speed limit. Galaxies at the edge of the observable universe are receding faster than light. Their light will never reach us.' },
  { title: '95% invisible', text: 'Everything you can see or touch makes up about 5% of the universe. Roughly 27% is dark matter holding galaxies together, and 68% is dark energy driving accelerated expansion.' },
  { title: 'The Great Attractor', text: 'The Milky Way and millions of other galaxies are hurtling at 1.4 million mph toward a mysterious gravitational anomaly obscured by our own galaxy\u2019s dust.' },
  { title: 'The B\u00f6\u00f6tes Void', text: 'A spherical region of space 330 million light-years across, almost completely empty. If our galaxy were at its center, we wouldn\u2019t have known other galaxies existed until the 1960s.' },
  { title: 'Quantum entanglement', text: 'Two entangled particles instantly mirror each other\u2019s state across any distance. Einstein called it \u201cspooky action at a distance.\u201d' },
  { title: 'The Bubble Multiverse', text: 'Eternal inflation suggests pocket universes constantly form like bubbles in boiling water \u2014 each with its own laws of physics, completely isolated from the others.' },
  { title: 'False Vacuum Decay', text: 'If the Higgs field is in a \u201cfalse vacuum,\u201d it could drop to a lower energy state, creating a bubble of destruction expanding at light speed \u2014 rewriting the laws of physics instantly.' },
  { title: 'Time may not exist', text: 'In some quantum gravity approaches, time vanishes from the equations. It may not be a fundamental feature of the universe but an illusion created by entanglement and human perception.' },
  { title: 'The Great Filter', text: 'Given the universe\u2019s age and size, where is everyone? The Great Filter hypothesis suggests a catastrophic evolutionary hurdle almost no species survives. The question: is it behind us, or ahead?' },
]

// Shuffle on load
for (let i = WARP_FACTS.length - 1; i > 0; i--) {
  const j = Math.floor(Math.random() * (i + 1));
  [WARP_FACTS[i], WARP_FACTS[j]] = [WARP_FACTS[j], WARP_FACTS[i]]
}
let warpFactIdx = 0

function showWarpFact() {
  const el = document.getElementById('warp-fact')
  if (!el) return
  const fact = WARP_FACTS[warpFactIdx]
  warpFactIdx = (warpFactIdx + 1) % WARP_FACTS.length
  // Shuffle next
  if (warpFactIdx === 0) {
    for (let i = WARP_FACTS.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [WARP_FACTS[i], WARP_FACTS[j]] = [WARP_FACTS[j], WARP_FACTS[i]]
    }
  }
  el.innerHTML = `<strong>${fact.title}</strong> — ${fact.text}`
  el.classList.add('visible')
}

function hideWarpFact() {
  const el = document.getElementById('warp-fact')
  if (el) el.classList.remove('visible')
}


// ═══════════════════════════════════════════════════════════════
// DOM + INTRO
// ═══════════════════════════════════════════════════════════════

const landing = document.getElementById('landing')
const introPhase = document.getElementById('intro-phase')
const ctaPhase = document.getElementById('cta-phase')
const dot = document.querySelector('.pale-blue-dot')
const introAge = document.getElementById('intro-age')
const introPrompt = document.getElementById('intro-prompt')
const birthdateInput = document.getElementById('birthdate')
const beginBtn = document.getElementById('begin')
const birthMonth = document.getElementById('birth-month')
const birthDay = document.getElementById('birth-day')
const birthYear = document.getElementById('birth-year')

;(() => {
  if (!birthMonth || !birthDay || !birthYear || !birthdateInput) return
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const now = new Date()
  const curYear = now.getFullYear()

  months.forEach((m, i) => {
    const o = document.createElement('option')
    o.value = String(i + 1).padStart(2, '0')
    o.textContent = m
    birthMonth.appendChild(o)
  })
  for (let d = 1; d <= 31; d++) {
    const o = document.createElement('option')
    o.value = String(d).padStart(2, '0')
    o.textContent = d
    birthDay.appendChild(o)
  }
  for (let y = curYear; y >= 1900; y--) {
    const o = document.createElement('option')
    o.value = y
    o.textContent = y
    birthYear.appendChild(o)
  }

  const def = birthdateInput.value || '1934-11-09'
  const [dy, dm, dd] = def.split('-')
  birthMonth.value = dm
  birthDay.value = dd
  birthYear.value = dy

  function syncDate() {
    const m = birthMonth.value, d = birthDay.value, y = birthYear.value
    if (m && d && y) {
      birthdateInput.value = `${y}-${m}-${d}`
      if (beginBtn) beginBtn.classList.add('ready')
    } else {
      birthdateInput.value = ''
      if (beginBtn) beginBtn.classList.remove('ready')
    }
  }

  birthMonth.addEventListener('change', syncDate)
  birthDay.addEventListener('change', syncDate)
  birthYear.addEventListener('change', syncDate)
  syncDate()
})()

// Carl Sagan narration toggle
const saganBtn = document.getElementById('sagan-btn')
let saganAudio = null
let saganBgMusic = null  // intro song looping quietly under Sagan's VO
const pbdSequence = new PaleBlueDotSequence()

function stopSaganSequence() {
  if (saganAudio && !saganAudio.paused) saganAudio.pause()
  if (saganAudio) saganAudio.currentTime = SAGAN_START
  if (saganBtn) {
    saganBtn.classList.remove('playing')
    const icon = saganBtn.querySelector('.sagan-icon')
    if (icon) icon.textContent = '\u25B6'
  }
  pbdSequence.stop()
  // Fade out the intro song that was playing as ambient background.
  if (saganBgMusic) {
    fadeOutAudio(saganBgMusic, FADE_MS)
    saganBgMusic = null
  }
}

if (saganBtn) {
  saganBtn.addEventListener('click', () => {
    if (saganAudio && !saganAudio.paused) {
      stopSaganSequence()
      return
    }

    // Fade out whatever music is currently playing \u2014 the page's intro
    // music, a planet track, anything. Sagan's voice gets a clean
    // background, and the intro song will fade back in (low volume) as
    // ambient bed once the VO begins.
    if (currentAudio) {
      fadeOutAudio(currentAudio, 1400)
      currentAudio = null
    }

    if (!saganAudio) {
      saganAudio = new Audio()
      saganAudio.crossOrigin = 'anonymous'
      saganAudio.src = `${MUSIC_BASE}/carl_sagan.mp3`
      saganAudio.addEventListener('ended', () => {
        saganBtn.classList.remove('playing')
        saganBtn.querySelector('.sagan-icon').textContent = '\u25B6'
        if (saganBgMusic) {
          fadeOutAudio(saganBgMusic, FADE_MS)
          saganBgMusic = null
        }
      })
    }

    // Once Sagan's VO begins, start Intro.mp3 at standard music volume.
    // The track has its own fade-in baked into the file — no JS fade.
    const startSaganPlayback = () => {
      saganAudio.currentTime = SAGAN_START
      setTimeout(() => {
        saganAudio.play().catch(() => {})
        if (musicEnabled) {
          const bg = createConnectedAudio(`${MUSIC_BASE}/Intro.mp3`)
          bg.loop = true
          bg._trackSrc = `${MUSIC_BASE}/Intro.mp3`
          bg.volume = 0.3
          bg.play().catch(() => {})
          saganBgMusic = bg
        }
      }, 3000)
    }

    // Play+pause unlocks playback under the user gesture; seek to the
    // iconic-line offset, hold 3s, then resume \u2014 all while the visual
    // sequence's camera blend has been running on wall-clock since click.
    saganAudio.play()
      .then(() => { saganAudio.pause(); startSaganPlayback() })
      .catch(() => { startSaganPlayback() })

    saganBtn.classList.add('playing')
    saganBtn.querySelector('.sagan-icon').textContent = '\u23F8'
    pbdSequence.start(saganAudio)
  })
}

// Return button \u2014 visible only during the Pale Blue Dot sequence.
const pbdReturnBtn = document.getElementById('pbd-return')
if (pbdReturnBtn) {
  pbdReturnBtn.addEventListener('click', stopSaganSequence)
}

async function runIntro() {
  if (!cosmicScene) return

  // Start loading Earth in background immediately
  Promise.resolve().then(async () => {
    try {
      const earthGroup = await cosmicScene.loadBody('earth')
      if (earthGroup) {
        cosmicScene.showBody(earthGroup, 'earth')
        cosmicScene.darkenLights()
      }
    } catch (e) {
      warn('runIntro: Earth load failed', e)
    }
  })

  const hasVisited = localStorage.getItem('cosmic-visited')

  // Phase 1: Pale blue dot fades in, then "tap to begin"
  await sleep(500)
  if (dot) dot.classList.add('visible')

  const enterEl = document.getElementById('intro-enter')
  const musicBtn = document.getElementById('intro-music')
  const silentBtn = document.getElementById('intro-silent')
  const skipBtn = document.getElementById('intro-skip')
  if (hasVisited && skipBtn) skipBtn.classList.add('visible')

  await sleep(1500)
  if (enterEl) enterEl.classList.add('visible')

  // Wait for user to choose — this gesture unlocks audio context
  const choice = await new Promise(resolve => {
    if (musicBtn) musicBtn.addEventListener('click', () => resolve('music'), { once: true })
    if (silentBtn) silentBtn.addEventListener('click', () => resolve('silent'), { once: true })
    if (skipBtn) skipBtn.addEventListener('click', () => resolve('skip'), { once: true })
  })
  if (enterEl) enterEl.classList.add('clicked')

  const wantsMusic = choice === 'music'
  const skipIntro = choice === 'skip'
  musicEnabled = wantsMusic || skipIntro
  _initAudioPool()

  if (wantsMusic) {
    const el = document.documentElement
    const rfs = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen
    if (rfs) rfs.call(el).catch(() => {})
  }

  if (musicEnabled) {
    const introAudio = createConnectedAudio(`${MUSIC_BASE}/Intro.mp3`)
    introAudio.loop = true
    introAudio._trackSrc = `${MUSIC_BASE}/Intro.mp3`
    currentAudio = introAudio
    fadeInAudio(introAudio, 0.3, 3000)
  }

  localStorage.setItem('cosmic-visited', '1')

  if (skipIntro) {
    if (introPhase) introPhase.classList.add('hidden')
    await sleep(400)
    if (landing) landing.classList.add('reveal-earth')
    if (cosmicScene && cosmicScene.fadeInLights) cosmicScene.fadeInLights('earth', 1500)
    await sleep(800)
    if (ctaPhase) ctaPhase.classList.add('visible')
    if (window._showBottomBar) window._showBottomBar()
    await sleep(500)
    if (cosmicScene) { cosmicScene.enableControls(); cosmicScene.markCameraHome() }
    ctaActive = true
    showInteractHint()
    return
  }

  // Phase 2: Earth age line
  await sleep(1000)
  if (introAge) {
    introAge.innerHTML = 'The Pale Blue Dot has been on the cosmic stage for<br><strong>4.5 Billion Years</strong>'
    introAge.classList.add('visible')
  }

  // Phase 3: Fade out age, show prompt
  await sleep(4000)
  if (introAge) introAge.classList.remove('visible')
  await sleep(2000)
  if (introPrompt) introPrompt.classList.add('visible')

  // Phase 4: Fade to black, then reveal Earth + CTA
  await sleep(3000)
  if (introPhase) introPhase.classList.add('hidden')

  await sleep(2000)
  if (landing) landing.classList.add('reveal-earth')
  if (cosmicScene && cosmicScene.fadeInLights) cosmicScene.fadeInLights('earth', 3000)

  await sleep(1500)
  if (ctaPhase) ctaPhase.classList.add('visible')
  if (window._showBottomBar) window._showBottomBar()

  await sleep(1500)
  if (cosmicScene) {
    cosmicScene.enableControls()
    cosmicScene.markCameraHome()
  }
  ctaActive = true
  showInteractHint()
}

// ═══════════════════════════════════════════════════════════════
// LANDING → JOURNEY (user clicks Launch)
// ═══════════════════════════════════════════════════════════════

beginBtn.addEventListener('click', async () => {
  if (!birthdateInput.value || !beginBtn.classList.contains('ready')) return
  hideInteractHint()
  beginBtn.classList.remove('ready')
  beginBtn.textContent = 'Launching…'

  userBirthStr = birthdateInput.value
  buildJourney(userBirthStr)

  // Start Gemini content generation for the first body
  const firstBody = BODIES[0]
  const needsDynamic = !firstBody.isStardust && !firstBody.isBlackHole
  const firstContentPromise = needsDynamic ? generateDynamicContent(firstBody.id) : null

  ctaActive = false
  cosmicScene.disableControls()
  returnBtn.classList.remove('visible')
  ctaPhase.classList.remove('visible')

  if (saganAudio && !saganAudio.paused) {
    saganAudio.pause()
    saganAudio.currentTime = 0
  }

  await sleep(600)

  cosmicScene.darkenLights()
  showWarpFact()
  const warpStart = performance.now()
  await warpTransition.engage()

  landing.classList.add('hidden')

  let loadedGroup = null

  if (firstBody.isBlackHole) {
    cosmicScene.removeCurrentBody()
    document.getElementById('scene-canvas').style.opacity = '0'
    document.getElementById('scene-canvas').style.pointerEvents = 'none'
    const container = document.createElement('div')
    container.id = 'blackhole-container'
    document.body.appendChild(container)
    try {
      const { mount } = await import('../Black Hole/sources/mount.js')
      blackHoleInstance = mount(container)
      attachBlackHoleDriftDetection(container)
      document.body.classList.add('blackhole-active')
    } catch (e) { warn('Black Hole mount failed', e) }
  } else if (firstBody.sceneId) {
    const loadingEl = document.getElementById('loading-indicator')
    loadingEl.textContent = `Loading ${firstBody.name}…`
    loadingEl.classList.add('visible')
    const group = await cosmicScene.loadBody(firstBody.sceneId, (p) => {
      loadingEl.textContent = `Loading ${firstBody.name} ${Math.round(p * 100)}%`
    })
    loadingEl.classList.remove('visible')
    loadingEl.textContent = 'Loading…'
    loadedGroup = group
  }

  const MUSIC_FADE_AT_MS = 8000
  const elapsedBeforeFade = performance.now() - warpStart
  const fadeDelay = Math.max(0, MUSIC_FADE_AT_MS - elapsedBeforeFade)
  await sleep(fadeDelay)
  crossfadeToBody(firstBody.id)

  const elapsed = performance.now() - warpStart
  const remaining = Math.max(0, MIN_WARP_HOLD_MS - elapsed)
  if (remaining > 0) await sleep(remaining)

  if (loadedGroup && firstBody.sceneId) {
    cosmicScene.showBody(loadedGroup, firstBody.sceneId)
  }

  // Hold warp until Gemini content is ready (or we hit the cap), so the
  // slide reveals with content rather than "Consulting the cosmos…"
  if (firstContentPromise) {
    const MAX_WARP_TOTAL_MS = 18000
    const elapsedForContent = performance.now() - warpStart
    const remainingBudget = Math.max(0, MAX_WARP_TOTAL_MS - elapsedForContent)
    if (remainingBudget > 0) {
      await Promise.race([
        firstContentPromise.catch(() => null),
        sleep(remainingBudget)
      ])
    }
  }

  hideWarpFact()
  await warpTransition.disengage()

  journeyActive = true
  document.getElementById('journey').classList.add('active')
  showNavUI()
  if (window._showBottomBar) window._showBottomBar()

  await goToSlide(0)

  if (firstBody.sceneId) {
    cosmicScene.fadeInLights(firstBody.sceneId)
  }

  if (firstBody.isBlackHole) {
    setTimeout(() => {
      const bhMb = document.getElementById('bh-mobile-restart')
      if (bhMb) bhMb.classList.add('visible')
    }, 1500)
  }

})

// ═══════════════════════════════════════════════════════════════
// INIT
// ═══════════════════════════════════════════════════════════════

log('Init: querying canvas')
const sceneCanvas = document.getElementById('scene-canvas')
const warpCanvas = document.getElementById('warp-canvas')
if (!sceneCanvas) {
  err('Init: #scene-canvas not found')
  document.body.innerHTML = '<div style="padding:2rem;color:#fff;font-family:sans-serif;background:#111;">' +
    '<h1>Load error</h1><p>Scene canvas element not found. Check that the page loaded correctly.</p></div>'
} else {
  try {
    log('Init: creating CosmicScene')
    cosmicScene = new CosmicScene(sceneCanvas)
    log('Init: CosmicScene created')
    pbdSequence.cosmicScene = cosmicScene
    warpTransition = new WarpTransition(warpCanvas || document.createElement('canvas'))
    log('Init: WarpTransition created')
    setupDriftDetection()
    log('Init: calling runIntro()')
    runIntro()
    log('Init: runIntro() called (async)')
  } catch (e) {
    err('Init: exception', e)
    document.body.innerHTML = '<div style="padding:2rem;color:#fff;font-family:sans-serif;background:#111;max-width:40rem;">' +
      '<h1>Init error</h1><p>' + (e && e.message ? e.message : String(e)) + '</p>' +
      '<p>Check the browser console for details.</p></div>'
  }
}

// ═══════════════════════════════════════════════════════════════
// CONTROLS PANEL
// ═══════════════════════════════════════════════════════════════

function initControlsPanel() {
  // Scene progress indicator
  const progressContainer = document.getElementById('nav-progress')
  if (progressContainer) {
    const track = document.createElement('div')
    track.className = 'nav-progress-track'
    BODIES.forEach((body, i) => {
      const dot = document.createElement('div')
      dot.className = 'nav-progress-dot'
      dot.dataset.name = body.name
      dot.dataset.index = i
      track.appendChild(dot)
    })
    progressContainer.appendChild(track)

    track.addEventListener('click', (e) => {
      const dot = e.target.closest('.nav-progress-dot')
      if (!dot) return
      const navCurrent = document.getElementById('nav-current')
      if (navCurrent?.disabled) return
      const idx = parseInt(dot.dataset.index, 10)
      if (isNaN(idx) || idx === currentSlide) return
      if (transitioning) return
      goToSlide(idx, { forceWarp: !journeyActive })
    })
  }

  // About
  const aboutToggle = document.getElementById('about-toggle')
  const aboutModal = document.getElementById('about-modal')
  const aboutClose = document.getElementById('about-close')
  const aboutBody = document.getElementById('about-body')
  if (aboutToggle && aboutModal) {
    aboutBody.innerHTML = renderCredits(CREDITS_MD)
    aboutToggle.addEventListener('click', () => aboutModal.classList.add('visible'))
    aboutClose.addEventListener('click', () => aboutModal.classList.remove('visible'))
    aboutModal.addEventListener('click', e => { if (e.target === aboutModal) aboutModal.classList.remove('visible') })
  }

  // Studio image lightbox
  const lightbox = document.getElementById('img-lightbox')
  const lightboxImg = document.getElementById('img-lightbox-img')
  if (lightbox) {
    document.querySelectorAll('.about-studio-thumb, .finale-studio-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        lightboxImg.src = thumb.src
        lightboxImg.alt = thumb.alt
        lightbox.classList.add('visible')
      })
    })
    lightbox.addEventListener('click', () => lightbox.classList.remove('visible'))
  }

  // Scene ribbon flyout
  const navCurrent = document.getElementById('nav-current')
  const ribbon = document.getElementById('scene-ribbon')
  const ribbonTrack = document.getElementById('scene-ribbon-track')
  if (navCurrent && ribbon && ribbonTrack) {
    BODIES.forEach((body, i) => {
      const btn = document.createElement('button')
      btn.className = 'scene-ribbon-item'
      btn.textContent = body.name
      btn.dataset.index = i
      ribbonTrack.appendChild(btn)
    })

    navCurrent.addEventListener('click', (e) => {
      e.stopPropagation()
      const isOpen = ribbon.classList.toggle('open')
      navCurrent.classList.toggle('open', isOpen)
      if (isOpen) {
        const active = ribbonTrack.querySelector('.active')
        if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    })

    document.addEventListener('click', (e) => {
      if (!ribbon.contains(e.target) && e.target !== navCurrent) {
        ribbon.classList.remove('open')
        navCurrent.classList.remove('open')
      }
    })

    let lastJumpTime = 0
    ribbonTrack.addEventListener('click', (e) => {
      const item = e.target.closest('.scene-ribbon-item')
      if (!item) return
      const now = Date.now()
      if (now - lastJumpTime < 600) return
      lastJumpTime = now

      const idx = parseInt(item.dataset.index, 10)
      log('Ribbon jump to scene:', idx)
      if (isNaN(idx)) return

      ribbon.classList.remove('open')
      navCurrent.classList.remove('open')

      let fromCTA = false
      if (!journeyActive) {
        fromCTA = true
        const date = document.getElementById('birthdate')?.value || '2000-01-01'
        buildJourney(date)
        journeyActive = true

        ctaActive = false
        ctaPhase.classList.remove('visible')
        if (saganAudio && !saganAudio.paused) {
          saganAudio.pause()
          saganAudio.currentTime = 0
        }
        const landingEl = document.getElementById('landing')
        if (landingEl) landingEl.classList.add('hidden')

        document.getElementById('journey').classList.add('active')
        showNavUI()
        if (window._showBottomBar) window._showBottomBar()
      }

      hideFinale()

      if (transitioning) {
        warn('Force-resetting transition for explicit scene jump')
        transitioning = false
        transitionStartedAt = 0
        transitionVersion++
      }

      goToSlide(idx, { forceWarp: fromCTA })
    })
  }

  // Music & Warp pills (separate floating elements)
  const musicPill = document.getElementById('ctrl-music-toggle')
  const warpPill = document.getElementById('ctrl-visualizer')
  const allTracks = [
    ['earth', 'Intro'], ['sun', 'Helios'], ['mercury', 'Mercury'],
    ['venus', 'Venus'], ['the_moon', 'Luna'], ['mars', 'Ares'],
    ['ceres', 'Ceres'], ['jupiter', 'Jupiter'], ['europa', 'Europa'],
    ['ganymede', 'Ganymede'], ['saturn', 'Saturn'], ['uranus', 'Ouranos'],
    ['neptune', 'Neptune'], ['solarsystem', 'Orrery'],
    ['stardust', 'Stardust'], ['blackhole', 'BlackHole'],
  ]

  const musicPillText = document.getElementById('music-pill-text')

  function updateMusicUI() {
    if (!musicPill) return
    const isPlaying = currentAudio && !currentAudio.paused
    musicPill.classList.toggle('playing', !!isPlaying)
    if (musicPillText) {
      if (isPlaying) {
        const entry = currentAudio._trackSrc
          ? allTracks.find(([, v]) => currentAudio._trackSrc.includes(v))
          : null
        musicPillText.textContent = entry ? entry[1] : 'Playing'
      } else {
        musicPillText.textContent = 'Paused'
      }
    }
  }
  window._updateMusicUI = updateMusicUI
  window._showBottomBar = () => {
    if (musicPill) musicPill.classList.add('visible')
    if (warpPill) warpPill.classList.add('visible')
    if (fsBtn) fsBtn.classList.add('visible')
    const coffeeBtn = document.getElementById('ctrl-coffee')
    if (coffeeBtn) coffeeBtn.classList.add('visible')
  }

  if (musicPill) {
    musicPill.addEventListener('click', () => {
      if (currentAudio && !currentAudio.paused) {
        currentAudio.pause()
      } else if (currentAudio && currentAudio.paused) {
        musicEnabled = true
        currentAudio.play().catch(() => {})
      } else {
        musicEnabled = true
        let played = false
        if (journeyActive && BODIES[currentSlide]) {
          playTrackForBody(BODIES[currentSlide].id)
          played = !!currentAudio
        }
        if (!played) {
          const audio = createConnectedAudio(`${MUSIC_BASE}/Intro.mp3`)
          audio.loop = true
          audio._trackSrc = `${MUSIC_BASE}/Intro.mp3`
          currentAudio = audio
          fadeInAudio(audio, 0.3, FADE_MS)
        }
      }
      updateMusicUI()
    })
  }

  setInterval(updateMusicUI, 2000)

  // Fullscreen button (with iOS webkit fallback)
  const fsBtn = document.getElementById('ctrl-fullscreen')
  if (fsBtn) {
    fsBtn.addEventListener('click', () => {
      const doc = document
      const el = doc.documentElement
      const isFS = doc.fullscreenElement || doc.webkitFullscreenElement
      if (isFS) {
        if (doc.exitFullscreen) doc.exitFullscreen()
        else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen()
      } else {
        const rfs = el.requestFullscreen || el.webkitRequestFullscreen
        if (rfs) rfs.call(el).catch(() => {})
      }
    })
  }

  // ─── Music Visualizer Mode ───
  const vizBtn = document.getElementById('ctrl-visualizer')
  const vizOverlay = document.getElementById('visualizer')
  const vizClose = document.getElementById('viz-close')
  const vizPlay = document.getElementById('viz-play')
  const vizPrev = document.getElementById('viz-prev')
  const vizNext = document.getElementById('viz-next')
  const vizTrackName = document.getElementById('viz-np-track')
  const vizFactTitle = document.getElementById('viz-fact-title')
  const vizFactText = document.getElementById('viz-fact-text')
  const vizFactArea = vizFactTitle?.closest('.viz-fact-area')
  const vizTracklist = document.getElementById('viz-tracklist')

  let vizActive = false
  let vizFactTimer = null
  let vizFactIndex = 0
  let vizIdleTimer = null

  function vizResetIdle() {
    if (!vizActive) return
    vizOverlay.classList.remove('idle')
    clearTimeout(vizIdleTimer)
    vizIdleTimer = setTimeout(() => {
      if (vizActive) vizOverlay.classList.add('idle')
    }, 3000)
  }

  function buildVizTracklist() {
    if (!vizTracklist) return
    vizTracklist.innerHTML = ''
    allTracks.forEach(([, trackName], idx) => {
      const btn = document.createElement('button')
      btn.className = 'viz-track'
      btn.textContent = trackName
      btn.dataset.idx = idx
      btn.addEventListener('click', () => {
        vizPlayTrack(idx)
      })
      vizTracklist.appendChild(btn)
    })
  }

  function vizPlayTrack(idx) {
    if (idx < 0 || idx >= allTracks.length) return
    const [, tName] = allTracks[idx]
    musicEnabled = true
    fadeOutAudio(currentAudio, 400)
    const audio = createConnectedAudio(`${MUSIC_BASE}/${tName}.mp3`)
    audio.loop = true
    audio._trackSrc = `${MUSIC_BASE}/${tName}.mp3`
    currentAudio = audio
    fadeInAudio(audio, 0.45, 800)
    updateVizUI()
    setTimeout(updateMusicUI, 200)
  }

  function updateVizUI() {
    if (!vizTrackName) return
    const entry = currentAudio?._trackSrc
      ? allTracks.find(([, v]) => currentAudio._trackSrc.includes(v))
      : null
    vizTrackName.textContent = entry ? entry[1] : '—'
    vizPlay.innerHTML = (currentAudio && !currentAudio.paused) ? '&#9646;&#9646;' : '&#9654;'

    if (vizTracklist) {
      vizTracklist.querySelectorAll('.viz-track').forEach((btn, i) => {
        const active = entry && allTracks[i][1] === entry[1]
        btn.classList.toggle('active', !!active)
      })
    }
  }

  function showVizFact() {
    if (!vizFactArea) return
    const fact = WARP_FACTS[vizFactIndex]
    vizFactIndex = (vizFactIndex + 1) % WARP_FACTS.length

    vizFactArea.classList.add('fading')
    setTimeout(() => {
      vizFactTitle.textContent = fact.title
      vizFactText.textContent = fact.text
      vizFactArea.classList.remove('fading')
    }, 1000)
  }

  function enterVisualizer() {
    if (vizActive) return
    vizActive = true

    buildVizTracklist()

    vizFactIndex = 0
    vizFactTitle.textContent = WARP_FACTS[0].title
    vizFactText.textContent = WARP_FACTS[0].text
    vizFactIndex = 1

    if (warpTransition) {
      warpTransition.hold()
    }
    vizOverlay.classList.add('active')
    vizOverlay.classList.remove('idle')

    const journey = document.getElementById('journey')
    const nav = document.getElementById('nav')
    const editor = document.getElementById('editor-area')
    const scCanvas = document.getElementById('scene-canvas')
    const prog = document.getElementById('nav-progress')
    if (journey) journey.style.opacity = '0'
    if (nav) nav.style.opacity = '0'
    if (editor) editor.style.opacity = '0'
    if (scCanvas) scCanvas.style.opacity = '0'
    if (prog) prog.style.opacity = '0'
    if (musicPill) musicPill.style.opacity = '0'
    if (fsBtn) fsBtn.style.opacity = '0'
    if (warpPill) warpPill.style.opacity = '0'
    if (ctaPhase) ctaPhase.style.opacity = '0'

    // Always start on Intro
    musicEnabled = true
    const introIdx = allTracks.findIndex(([, v]) => v === 'Intro')
    vizPlayTrack(introIdx >= 0 ? introIdx : 0)

    updateVizUI()
    vizFactTimer = setInterval(showVizFact, 8000)

    // Start idle detection
    document.addEventListener('mousemove', vizResetIdle)
    vizResetIdle()
  }

  function exitVisualizer() {
    if (!vizActive) return
    vizActive = false

    if (saganAudio && !saganAudio.paused) {
      saganAudio.pause()
      saganAudio.currentTime = 0
      if (vizSaganBtn) { vizSaganBtn.classList.remove('playing'); vizSaganBtn.querySelector('.viz-sagan-icon').textContent = '\u25B6' }
      if (vizMSagan) { vizMSagan.classList.remove('playing'); vizMSagan.querySelector('.viz-sagan-icon').textContent = '\u25B6' }
      if (saganBtn) { saganBtn.classList.remove('playing'); saganBtn.querySelector('.sagan-icon').textContent = '\u25B6' }
    }

    // Clean up idle detection
    document.removeEventListener('mousemove', vizResetIdle)
    clearTimeout(vizIdleTimer)
    vizOverlay.classList.remove('idle')

    if (vizFactTimer) { clearInterval(vizFactTimer); vizFactTimer = null }
    vizOverlay.classList.remove('active')

    const journey = document.getElementById('journey')
    const nav = document.getElementById('nav')
    const editor = document.getElementById('editor-area')
    const scCanvas = document.getElementById('scene-canvas')
    const prog = document.getElementById('nav-progress')
    if (journey) journey.style.opacity = ''
    if (nav) nav.style.opacity = ''
    if (editor) editor.style.opacity = ''
    if (scCanvas) scCanvas.style.opacity = ''
    if (prog) prog.style.opacity = ''
    if (musicPill) musicPill.style.opacity = ''
    if (fsBtn) fsBtn.style.opacity = ''
    if (warpPill) warpPill.style.opacity = ''
    if (ctaPhase) ctaPhase.style.opacity = ''

    if (warpTransition) {
      warpTransition.release()
    }

    // Crossfade back to the current scene's track
    if (journeyActive && BODIES[currentSlide]) {
      crossfadeToBody(BODIES[currentSlide].id)
    }
  }

  window._vizActive = () => vizActive
  if (vizBtn) vizBtn.addEventListener('click', enterVisualizer)
  if (vizClose) vizClose.addEventListener('click', exitVisualizer)

  if (vizPlay) {
    vizPlay.addEventListener('click', () => {
      if (currentAudio && !currentAudio.paused) {
        currentAudio.pause()
      } else if (currentAudio && currentAudio.paused) {
        musicEnabled = true
        currentAudio.play().catch(() => {})
      } else {
        vizPlayTrack(0)
      }
      updateVizUI()
      setTimeout(updateMusicUI, 200)
    })
  }

  if (vizPrev) {
    vizPrev.addEventListener('click', () => {
      const curIdx = currentAudio?._trackSrc
        ? allTracks.findIndex(([, v]) => currentAudio._trackSrc.includes(v))
        : 1
      vizPlayTrack((curIdx - 1 + allTracks.length) % allTracks.length)
    })
  }

  if (vizNext) {
    vizNext.addEventListener('click', () => {
      const curIdx = currentAudio?._trackSrc
        ? allTracks.findIndex(([, v]) => currentAudio._trackSrc.includes(v))
        : -1
      vizPlayTrack((curIdx + 1) % allTracks.length)
    })
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && vizActive) exitVisualizer()
  })

  // Mobile warp mode controls
  const vizMPlay = document.getElementById('viz-m-play')
  const vizMPrev = document.getElementById('viz-m-prev')
  const vizMNext = document.getElementById('viz-m-next')
  const vizMShuffle = document.getElementById('viz-m-shuffle')
  const vizMExit = document.getElementById('viz-m-exit')
  const vizMPlayText = vizMPlay?.querySelector('.viz-m-play-text')

  function updateMobileVizUI() {
    if (!vizMPlay) return
    const isPlaying = currentAudio && !currentAudio.paused
    vizMPlay.classList.toggle('playing', !!isPlaying)
    if (vizMPlayText) {
      if (isPlaying) {
        const entry = currentAudio._trackSrc
          ? allTracks.find(([, v]) => currentAudio._trackSrc.includes(v))
          : null
        vizMPlayText.textContent = entry ? entry[1] : 'Playing'
      } else {
        vizMPlayText.textContent = 'Paused'
      }
    }
  }

  const origUpdateVizUI = updateVizUI
  updateVizUI = function() { origUpdateVizUI(); updateMobileVizUI() }

  if (vizMPlay) vizMPlay.addEventListener('click', () => {
    if (currentAudio && !currentAudio.paused) { currentAudio.pause() }
    else if (currentAudio && currentAudio.paused) { musicEnabled = true; currentAudio.play().catch(() => {}) }
    else { vizPlayTrack(0) }
    updateVizUI()
    setTimeout(updateMusicUI, 200)
  })
  if (vizMPrev) vizMPrev.addEventListener('click', () => {
    const curIdx = currentAudio?._trackSrc ? allTracks.findIndex(([, v]) => currentAudio._trackSrc.includes(v)) : 1
    vizPlayTrack((curIdx - 1 + allTracks.length) % allTracks.length)
  })
  if (vizMNext) vizMNext.addEventListener('click', () => {
    const curIdx = currentAudio?._trackSrc ? allTracks.findIndex(([, v]) => currentAudio._trackSrc.includes(v)) : -1
    vizPlayTrack((curIdx + 1) % allTracks.length)
  })
  if (vizMShuffle) vizMShuffle.addEventListener('click', () => {
    const randomIdx = Math.floor(Math.random() * allTracks.length)
    vizPlayTrack(randomIdx)
  })
  if (vizMExit) vizMExit.addEventListener('click', exitVisualizer)

  // Warp mode Sagan toggles (desktop + mobile)
  const vizSaganBtn = document.getElementById('viz-sagan')
  const vizMSagan = document.getElementById('viz-m-sagan')

  function toggleWarpSagan() {
    if (saganAudio && !saganAudio.paused) {
      saganAudio.pause()
      if (vizSaganBtn) { vizSaganBtn.classList.remove('playing'); vizSaganBtn.querySelector('.viz-sagan-icon').textContent = '\u25B6' }
      if (vizMSagan) { vizMSagan.classList.remove('playing'); vizMSagan.querySelector('.viz-sagan-icon').textContent = '\u25B6' }
      if (saganBtn) { saganBtn.classList.remove('playing'); saganBtn.querySelector('.sagan-icon').textContent = '\u25B6' }
    } else {
      if (!saganAudio) {
        saganAudio = new Audio()
        saganAudio.crossOrigin = 'anonymous'
        saganAudio.src = `${MUSIC_BASE}/carl_sagan.mp3`
        saganAudio.addEventListener('ended', () => {
          ;[vizSaganBtn, vizMSagan, saganBtn].forEach(b => { if (b) { b.classList.remove('playing'); const ic = b.querySelector('.viz-sagan-icon') || b.querySelector('.sagan-icon'); if (ic) ic.textContent = '\u25B6' } })
        })
      }
      saganAudio.play().catch(() => {})
      if (vizSaganBtn) { vizSaganBtn.classList.add('playing'); vizSaganBtn.querySelector('.viz-sagan-icon').textContent = '\u23F8' }
      if (vizMSagan) { vizMSagan.classList.add('playing'); vizMSagan.querySelector('.viz-sagan-icon').textContent = '\u23F8' }
      if (saganBtn) { saganBtn.classList.add('playing'); saganBtn.querySelector('.sagan-icon').textContent = '\u23F8' }
    }
  }
  if (vizSaganBtn) vizSaganBtn.addEventListener('click', toggleWarpSagan)
  if (vizMSagan) vizMSagan.addEventListener('click', toggleWarpSagan)
}

// ═══════════════════════════════════════════════════════════════
// SCENE EDITOR
// ═══════════════════════════════════════════════════════════════

let sceneEditorSync = null

function initSceneEditor(scene) {
  log('initSceneEditor: start')
  const mount = document.getElementById('editor-mount')
  if (!mount) {
    warn('initSceneEditor: missing mount, skipping')
    return
  }

  log('initSceneEditor: GUI creating')

  const gui = new GUI({ container: mount, title: 'Options', closeFolders: true })
  gui.close()

  const editorState = {
    lightIntensity: 2.0,
    ambientIntensity: 0.12,
    bloomStrength: 0.25,
    bloomThreshold: 0.95,
    bloomRadius: 0.5,
    rotationSpeed: 0.002,
    tilt: 0,
    bodyRotX: 0, bodyRotY: 0, bodyRotZ: 0,
    cameraDist: 7,
    cameraAngle: 0.5,
    camPosX: 0, camPosY: 0, camPosZ: 0,
    targetX: DEFAULT_TARGET.x, targetY: DEFAULT_TARGET.y, targetZ: DEFAULT_TARGET.z,
    exposure: 1.0,
    bgBrightness: 1.0,
    bgExposure: 1.55,
    metalness: 0,
    roughness: 0.7,
    showDistantSun: true,
    saveCameraPos() {
      const bodyId = BODIES[currentSlide]?.sceneId
      if (!bodyId || !BODY_3D[bodyId]) return
      const p = scene.camera.position
      BODY_3D[bodyId].cameraPos = { x: +p.x.toFixed(3), y: +p.y.toFixed(3), z: +p.z.toFixed(3) }
      editorState.camPosX = p.x
      editorState.camPosY = p.y
      editorState.camPosZ = p.z
      gui.controllersRecursive().forEach(c => c.updateDisplay())
    },
    clearCameraPos() {
      const bodyId = BODIES[currentSlide]?.sceneId
      if (!bodyId || !BODY_3D[bodyId]) return
      BODY_3D[bodyId].cameraPos = null
      editorState.camPosX = 0
      editorState.camPosY = 0
      editorState.camPosZ = 0
      gui.controllersRecursive().forEach(c => c.updateDisplay())
    },
    saveCameraTarget() {
      const bodyId = BODIES[currentSlide]?.sceneId
      if (!bodyId || !BODY_3D[bodyId]) return
      const t = scene.controls.target
      BODY_3D[bodyId].cameraTarget = { x: +t.x.toFixed(3), y: +t.y.toFixed(3), z: +t.z.toFixed(3) }
      editorState.targetX = t.x
      editorState.targetY = t.y
      editorState.targetZ = t.z
      gui.controllersRecursive().forEach(c => c.updateDisplay())
    },
    saveBodyRot() {
      const bodyId = BODIES[currentSlide]?.sceneId
      if (!bodyId || !scene.currentGroup) return
      const r = scene.currentGroup.rotation
      if (!BODY_3D[bodyId].bodyRot) BODY_3D[bodyId].bodyRot = {}
      BODY_3D[bodyId].bodyRot = { x: +r.x.toFixed(4), y: +r.y.toFixed(4), z: +r.z.toFixed(4) }
      editorState.bodyRotX = r.x
      editorState.bodyRotY = r.y
      editorState.bodyRotZ = r.z
      gui.controllersRecursive().forEach(c => c.updateDisplay())
    },
    clearBodyRot() {
      const bodyId = BODIES[currentSlide]?.sceneId
      if (!bodyId) return
      BODY_3D[bodyId].bodyRot = null
      editorState.bodyRotX = 0
      editorState.bodyRotY = 0
      editorState.bodyRotZ = 0
      gui.controllersRecursive().forEach(c => c.updateDisplay())
    },
    resetCamera() {
      const bodyId = BODIES[currentSlide]?.sceneId
      if (!bodyId) return
      const data = BODY_3D[bodyId]
      if (data?.cameraPos) {
        scene.camera.position.set(data.cameraPos.x, data.cameraPos.y, data.cameraPos.z)
      }
      const tgt = data?.cameraTarget || DEFAULT_TARGET
      scene.controls.target.set(tgt.x, tgt.y, tgt.z)
      scene.controls.update()
    },
    rebuildStars() {
      scene.rebuildStarField()
    },
    async goToSelectedScene() {
      let idx = editorState.currentScene
      if (typeof idx === 'string') idx = sceneOptions[idx]
      if (typeof idx !== 'number' || idx < 0 || idx >= BODIES.length) return
      const slides = slidesEl.querySelectorAll('.slide')
      if (slides.length === 0) {
        const date = birthdateInput?.value || '2000-01-01'
        buildJourney(date)
        ctaActive = false
        cosmicScene.disableControls()
        returnBtn.classList.remove('visible')
        ctaPhase.classList.remove('visible')
        await sleep(600)
        cosmicScene.darkenLights()
        showWarpFact()
        await warpTransition.engage()
        landing.classList.add('hidden')
        journeyActive = true
        document.getElementById('journey').classList.add('active')
        showNavUI()
        if (window._showBottomBar) window._showBottomBar()
        hideWarpFact()
        await warpTransition.disengage()
        await goToSlide(idx)
        if (BODIES[idx].sceneId) cosmicScene.fadeInLights(BODIES[idx].sceneId)
        return
      }
      await goToSlide(idx)
    },
    exportAll() {
      const bodies = Object.keys(BODY_3D).map(id => ({
        id,
        ...BODY_3D[id],
      }))
      const json = JSON.stringify({ bodies }, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'scene-settings.json'
      a.click()
      URL.revokeObjectURL(url)
    },
  }

  const fLight = gui.addFolder('Lighting')
  fLight.add(editorState, 'lightIntensity', 0, 5, 0.05).name('Sun').onChange(v => {
    scene.sunLight.intensity = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].lightIntensity = v
  })
  fLight.add(editorState, 'ambientIntensity', 0, 1, 0.01).name('Ambient').onChange(v => {
    scene.ambientLight.intensity = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].ambientIntensity = v
  })

  const fBloom = gui.addFolder('Bloom')
  fBloom.add(editorState, 'bloomStrength', 0, 3, 0.05).name('Strength').onChange(v => {
    scene.bloomPass.strength = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].bloomStrength = v
  })
  fBloom.add(editorState, 'bloomThreshold', 0, 1, 0.01).name('Threshold').onChange(v => {
    scene.bloomPass.threshold = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].bloomThreshold = v
  })
  fBloom.add(editorState, 'bloomRadius', 0, 2, 0.05).name('Radius').onChange(v => {
    scene.bloomPass.radius = v
  })

  function setBodyMaterialProp(prop, value) {
    if (!scene.currentGroup) return
    scene.currentGroup.traverse(child => {
      if (child.isMesh) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach(m => { if (prop in m) { m[prop] = value; m.needsUpdate = true } })
      }
    })
  }

  const fBody = gui.addFolder('Body')
  fBody.add(editorState, 'metalness', 0, 1, 0.01).name('Metalness').onChange(v => {
    setBodyMaterialProp('metalness', v)
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].metalness = v
  })
  fBody.add(editorState, 'roughness', 0, 1, 0.01).name('Roughness').onChange(v => {
    setBodyMaterialProp('roughness', v)
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].roughness = v
  })
  fBody.add(editorState, 'rotationSpeed', -0.01, 0.01, 0.0001).name('Rotation').onChange(v => {
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].rotationSpeed = v
  })
  fBody.add(editorState, 'tilt', -Math.PI, Math.PI, 0.01).name('Tilt').onChange(v => {
    if (scene.currentGroup) scene.currentGroup.rotation.z = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) BODY_3D[id].tilt = v
  })
  fBody.add(editorState, 'bodyRotX', -Math.PI, Math.PI, 0.01).name('Rot X').onChange(v => {
    if (scene.currentGroup) scene.currentGroup.rotation.x = v
  }).listen()
  fBody.add(editorState, 'bodyRotY', -Math.PI, Math.PI, 0.01).name('Rot Y').onChange(v => {
    if (scene.currentGroup) scene.currentGroup.rotation.y = v
  }).listen()
  fBody.add(editorState, 'bodyRotZ', -Math.PI, Math.PI, 0.01).name('Rot Z').onChange(v => {
    if (scene.currentGroup) scene.currentGroup.rotation.z = v
  }).listen()
  fBody.add(editorState, 'saveBodyRot').name('Save Body Rot')
  fBody.add(editorState, 'clearBodyRot').name('Clear Body Rot')

  const fCam = gui.addFolder('Camera')
  fCam.add(editorState, 'camPosX').name('Pos X').listen().disable()
  fCam.add(editorState, 'camPosY').name('Pos Y').listen().disable()
  fCam.add(editorState, 'camPosZ').name('Pos Z').listen().disable()
  fCam.add(editorState, 'saveCameraPos').name('Save Camera Pos')
  fCam.add(editorState, 'clearCameraPos').name('Clear Camera Pos')
  fCam.add(editorState, 'targetX', -10, 10, 0.1).name('Target X').onChange(v => {
    scene.controls.target.x = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) {
      BODY_3D[id].cameraTarget = BODY_3D[id].cameraTarget || { ...DEFAULT_TARGET }
      BODY_3D[id].cameraTarget.x = v
    }
  })
  fCam.add(editorState, 'targetY', -10, 10, 0.1).name('Target Y').onChange(v => {
    scene.controls.target.y = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) {
      BODY_3D[id].cameraTarget = BODY_3D[id].cameraTarget || { ...DEFAULT_TARGET }
      BODY_3D[id].cameraTarget.y = v
    }
  })
  fCam.add(editorState, 'targetZ', -10, 10, 0.1).name('Target Z').onChange(v => {
    scene.controls.target.z = v
    const id = BODIES[currentSlide]?.sceneId
    if (id && BODY_3D[id]) {
      BODY_3D[id].cameraTarget = BODY_3D[id].cameraTarget || { ...DEFAULT_TARGET }
      BODY_3D[id].cameraTarget.z = v
    }
  })
  fCam.add(editorState, 'saveCameraTarget').name('Save Target')
  fCam.add(editorState, 'resetCamera').name('Reset Camera')

  const fGlobal = gui.addFolder('Global')
  fGlobal.add(editorState, 'exposure', 0.1, 3, 0.05).name('Exposure').onChange(v => {
    scene.renderer.toneMappingExposure = v
  })
  fGlobal.add(editorState, 'bgBrightness', 0, 2, 0.01).name('BG Brightness').onChange(v => {
    if (scene.skyboxMesh?.material?.uniforms?.uBrightness)
      scene.skyboxMesh.material.uniforms.uBrightness.value = v
  })
  fGlobal.add(editorState, 'bgExposure', 0.1, 3, 0.05).name('BG Exposure').onChange(v => {
    if (scene.skyboxMesh?.material?.uniforms?.uExposure)
      scene.skyboxMesh.material.uniforms.uExposure.value = v
  })
  fGlobal.add(editorState, 'showDistantSun').name('Distant Sun').onChange(v => {
    if (scene.distantSunGroup) scene.distantSunGroup.visible = v
  })
  fGlobal.add(editorState, 'rebuildStars').name('Rebuild Star Field')

  const sceneOptions = {}
  BODIES.forEach((b, i) => { if (b.sceneId) sceneOptions[b.name] = i })
  editorState.currentScene = BODIES.findIndex(b => b.sceneId) >= 0 ? BODIES.findIndex(b => b.sceneId) : 0
  gui.add(editorState, 'currentScene', sceneOptions).name('Go To Scene')
  gui.add(editorState, 'goToSelectedScene').name('Go')

  gui.add(editorState, 'exportAll').name('Export Settings')

  function syncEditorToBody() {
    const bodyId = BODIES[currentSlide]?.sceneId
    if (!bodyId || !BODY_3D[bodyId]) return
    const data = BODY_3D[bodyId]
    editorState.currentScene = currentSlide
    editorState.lightIntensity = data.lightIntensity ?? 2
    editorState.ambientIntensity = data.ambientIntensity ?? 0.12
    editorState.bloomStrength = data.bloomStrength ?? 0.25
    editorState.bloomThreshold = data.bloomThreshold ?? 0.95
    editorState.bloomRadius = scene.bloomPass.radius
    editorState.rotationSpeed = data.rotationSpeed ?? 0.002
    editorState.tilt = data.tilt ?? 0
    editorState.metalness = data.metalness ?? 0
    editorState.roughness = data.roughness ?? 0.7
    editorState.camPosX = scene.camera.position.x
    editorState.camPosY = scene.camera.position.y
    editorState.camPosZ = scene.camera.position.z
    const tgt = data.cameraTarget || DEFAULT_TARGET
    editorState.targetX = tgt.x
    editorState.targetY = tgt.y
    editorState.targetZ = tgt.z
    if (scene.currentGroup) {
      editorState.bodyRotX = scene.currentGroup.rotation.x
      editorState.bodyRotY = scene.currentGroup.rotation.y
      editorState.bodyRotZ = scene.currentGroup.rotation.z
    }
    editorState.showDistantSun = scene.distantSunGroup ? scene.distantSunGroup.visible : true
    gui.controllersRecursive().forEach(c => c.updateDisplay())
  }

  sceneEditorSync = syncEditorToBody
  log('initSceneEditor: done')
}

if (cosmicScene) {
  initControlsPanel()
  initSceneEditor(cosmicScene)
  log('Init: complete')
} else {
  warn('Init: cosmicScene is null, skipping editor')
}
